// §4.4 — THE QUOTE COMPOSER, /sales/quotes/new and /sales/quotes/$quoteId
// (Module 09, slice P3-C-1). The screen a KazKabel sales manager spends their
// morning in, and the surface §10's M09-S03/S04 drive.
//
// ONE COMPONENT, TWO ROUTES, deliberately: "new" and "edit" are the same act
// with a different starting document, and §3.4 makes editing a SENT quote
// produce revision n+1 rather than a patch — so the edit path is a create path
// with lines pre-filled. Two components would be two grids, two price tables
// and two chances for them to disagree about what a line is.
//
// AND A THIRD ROUTE, /sales/orders/new — ORDER MODE (P3-D PR-D1, D-283,
// M09-S17). The same component with `document="order"`: the grid, the §3.2
// dry run, the off-cut panel, the currency guard and the tenant day are all
// reused in place, and what differs is the DOCUMENT — no validity, no notes
// (`sales_orders` has no such column), a facility picker (`fulfilling_
// facility_id`, "Choose automatically" = the §2.16 derivation; absent when
// the tenant has one active facility) and a customer-PO field; ONE verb whose
// label says whether the press confirms the order or parks it for approval;
// no draft step — the press is `POST /v1/orders` and lands on the order. The
// write gate is `sales.order.write`, not `sales.quote.write`. §4.4's mobile
// rule applies verbatim: editors and the place button are desktop-only.
//
// THE LINE GRID IS A FORM, AND IT STILL GOES THROUGH `DataTable`.
//
// The first cut of this screen hand-rolled a `<table>`, reasoning that the
// register's table primitive is for rows a reader scans and these rows are
// FIELDS. That was wrong twice over. It trips the D-107 design-system gate,
// which is absolute at zero and has no baseline to absorb it — but the gate is
// the symptom. The real cost is that a hand-rolled table silently opts out of
// everything the primitive knows: the phone card-collapse contract, the
// `data-label` rule, the header tone, the numeric alignment, and every wave
// affordance a later slice adds. A quote grid one tier below the customer list
// it sits beside is exactly the "not as deep as the industry best" failure
// this slice exists to avoid.
//
// `DataTable`'s `render` takes any node, so an editable cell is just a cell
// whose render returns an input. What the grid does NOT take is the wave-1
// props: no `sort` (a caret would reorder the line numbers a customer's PDF is
// printed in), no `selectable` (there is no bulk verb on a line), no `sticky`
// (the grid is short and sits inside a form). It follows the price-list-detail
// idiom for everything else — rows in local state, an add-row control beneath,
// a wholesale save.
//
// THE SERVER PRICES EVERY LINE. The composer never computes a price it then
// sends back: a line with no `unit_price_minor` resolves through §3.2 (tier
// list → customer override → off-cut class), and a line WITH one is a manual
// price the server flags `off_list` and re-checks against the §2.11 guard.
// A line the server already priced `manual` carries that figure forward into
// the next revision (`rowsFromQuote`) — restating the manager's own number,
// never computing one. What this screen computes is a PREVIEW of the line and document totals
// (lib/quotes.previewTotals) so a manager sees a number before the round trip;
// the moment a draft is saved, the server's own totals replace it. The preview
// says so on the glass rather than leaving the reader to wonder which they are
// looking at.
//
// MARGIN IS §7-OMITTED, AND OMISSION MEANS ABSENCE. A sales user without
// `sales.margin.read` gets NO margin block — not a masked figure, not a lock
// icon, not a dash. What they get is the always-present `margin_breached`
// boolean as §4.4's neutral "needs approval" flag, which is the only margin
// fact that role is entitled to. The column itself only exists when at least
// one line on the document carried the keys (`columnPresent`, the area's one
// omission rule).
//
// MOBILE IS READ + APPROVE/SEND ONLY (§4.4's own sentence). Line editing needs
// a keyboard and six columns; a phone gets the document, the totals and the
// verbs, plus a note saying why. That is asserted by the responsive net rather
// than implied by CSS: `useDesktop()` decides, so the editors are ABSENT from
// the DOM on a phone instead of present-but-unreachable.
//
// THE AGENT-DRAFT BANNER (origin='agent') renders the inbound MESSAGE inline,
// because a manager reviewing an agent's draft is really reviewing whether it
// answered what the customer actually asked, and making them open module 17 in
// another tab to find out is how agent drafts stop being reviewed.
//
// THE AGENT'S NOTE (P3-K, D-516 ruling 4). §4.4's "source message +
// reasoning" is the inbound message above and, when the revision carries one,
// the agent's own note in an "Agent's note" expander. The note sits inside
// the untrusted frame the journal card uses (components/untrusted-text.tsx),
// never styled as staff text and never copied into the internal note, and
// it is read with the language the gate recorded for it. A draft the agent
// wrote also carries an origin chip in the header, so a manager who scrolled
// past the banner still sees whose draft this is.

// THE VERBS SAY WHAT THE SERVER WILL DO — four corrections, 2026-09-10
// (P3-C-1 audit rows 30/32/33/82, and D-268's first ruling).
//
//  1. `accepted` IS TERMINAL HERE, as it is in the server's own TERMINAL set
//     (`quotes/quote-state.ts`). §3.4 names three terminals and `accepted` is
//     the momentary state between the token claim and `converted`; a document
//     being turned into an order is not one a manager may revise or cancel out
//     from under it, and this screen said otherwise.
//  2. NEEDS-REVIEW IS NOT THE SAME FACT AS BREACHED. `margin_breached` stays
//     true on the lines of a quote that has ALREADY been approved, so the
//     button on an approved document read "Send for approval" with the
//     approval hint under it while the server dispatched straight to the
//     customer. The label now mirrors the send path's own
//     `decidedForThisRevision` (approved, or this revision already sent), so
//     the control says what the press does. `pending_approval` deliberately
//     keeps the review label: the server answers that press with the review
//     card again, which is the truthful outcome.
//  3. MOBILE IS READ + APPROVE/SEND, and that now includes the WRITE VERBS.
//     Save was gated on `canWrite && !terminal` and Duplicate on `canWrite`
//     alone, so a phone offered "Save as a new version" over a document with
//     nothing editable — and a tap REVOKED the customer's live accept token
//     and superseded their link for a revision identical to the one they are
//     reading. Both now ride `editable` / `isDesktop`, and Save additionally
//     refuses when nothing is dirty: a no-op save of a sent quote is not a
//     no-op, it is a new revision.
//  4. AN EXPIRED QUOTE OFFERS ONLY "NEW VERSION" (D-268). `POST /send`
//     answers 409 `QUOTE_EXPIRED`: §3.4's `expired → sent` row requires
//     revision n+1 repriced at current lists, never a re-send of prices pinned
//     a fortnight ago on a fresh 14-day window. The screen offers the verb
//     that works and says why the other one is gone.
//
// THE LIFECYCLE VERBS §3.4 NAMES ARE ON THE DOCUMENT (row 82). Cancel and
// record-rejection are HUMAN transitions in §3.4's table with routes in §5,
// and until now the only caller of `POST /v1/quotes/{id}/cancel` in the repo
// was e2e cleanup — a manager who lost a deal had no way to say so, and a lost
// quote sat `sent` forever, reminding a customer who had already said no.
// Both are gated on `sales.quote.write` (the code their routes carry) with the
// "owner sales" narrowing left to the service, exactly as the order screen's
// cancel is: the client has no user id to compare owners with, and inventing a
// second authorisation rule here is how the two disagree. Both are DESKTOP
// verbs as well, because §4.4's mobile line admits read + approve/send and
// nothing else — and cancel is the least recoverable press on this screen, so
// a phone is the last place to offer it. The REVISION HISTORY
// (§5's `GET /revisions`) is collapsed and lazy — it is the answer to "what
// did version 1 say", which is a question asked after arriving, not on the way
// in.
//
// ATTACHED FILES sit above it on a saved quote (P3-E2 PR E2-2a2, spec 09
// §4.4's annotation, D-323 ruling 8): the quote's own files
// (components/attachments/attachments-panel.tsx), with Upload and Detach for a
// holder of `sales.quote.write`. A quote attachment follows its quote in a
// merge (§3.13), so the panel never names the customer.
//
// WHAT THE CUSTOMER WAS TOLD closes the document (P3-E PR-E1, D-291): a
// Notifications card over `GET /v1/customer-notifications?quote_id=` on every
// saved quote, draft included, rendered by §4.7's own table
// (components/customer-notifications.tsx) so a held, superseded or failed
// message reads the same on the quote as on its order (`QuoteNotificationsCard`,
// below).

import { useEffect, useMemo, useRef, useState } from "react";
import type { TFunction } from "i18next";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Banner } from "../../../../components/toast";
import { Card } from "../../../../components/card";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { RefLabel } from "../../../../components/ref-label";
import { Select } from "../../../../components/select";
import {
  CardSkeleton,
  EmptyState,
  ErrorState,
  ForbiddenState,
} from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { UntrustedText } from "../../../../components/untrusted-text";
import { useDesktop } from "../../../../components/use-desktop";
import { facilityTodayIso } from "../../../../lib/business-dates";
import { useTenantTimezone } from "../../../../lib/role-codes";
import { formatBusinessDate, formatTimestamp } from "../../../../lib/datetime";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { useDebouncedValue } from "../../../../lib/use-debounced-value";
import { useProduct, useUoms } from "../../catalogue/hooks/catalogue-queries";
import { CtpDialog, canRunCtp, type CtpQuoteResult } from "../../planning/public";
import { useProductSearch } from "../../production/hooks/production-queries";
import {
  columnPresent,
  formatMinor,
  minorToDecimal,
  minorUnitDigits,
  normalizeTypedDecimal,
} from "../lib/money";
import { decimalToMinor } from "../lib/price-import";
import { revisionAgentNote } from "../lib/agent-note";
import {
  canOpenQuotePdf,
  glyphCharactersSentence,
  glyphCodePointsOf,
  quotePdfPath,
} from "../lib/documents";
import {
  atpChipVariant,
  daysRemaining,
  lineTotalMinor,
  marginVisible,
  previewTotals,
  priceSourceVariant,
  QTY_TEXT,
  qtyTypedOk,
  quoteCustomerArchived,
  quoteCustomerQuotable,
  quoteStatusVariant,
  rejectedReasonParts,
  validityBand,
  type AtpBand,
} from "../lib/quotes";
import { CustomerNotificationTable } from "../components/customer-notifications";
import { OpenTasksCard } from "../components/sales-tasks";
import {
  NewQuoteDealLine,
  QuoteDealControl,
  createDealRefusalText,
  dealForCreate,
} from "../components/quote-deal";
import { MutationFailureNote } from "../mutation-failure-note";
import {
  linePatchFromQuote,
  quoteLinePromiseRefusal,
  type QuoteLinePromiseReason,
} from "../lib/promise-dates";
import { canAttachTo, isSalesReadRefused, useSalesPermissions } from "../hooks/permissions";
import { AttachmentsPanel } from "../components/attachments/attachments-panel";
import { isSalesValueRefusal, valueSortVerdict } from "../lib/value-order";
import {
  handOverOrderWarning,
  useCreateOrder,
  type OrderCreateRequest,
} from "../hooks/order-mutations";
import {
  useCustomer360,
  useCustomers,
  useOffcutClasses,
  useOwnerIndex,
  useResolvePrice,
  type PricingResolve,
  type PricingResolveRequest,
} from "../hooks/sales-queries";
import { useMemberRefAsk, useReadsHeldOffline } from "../lib/reads";
import { useSalesFacilities } from "./orders";
import { validateSalesComposerSearch } from "./search-params";
import {
  handOverDuplicateDiff,
  useArrivingDuplicateDiff,
  useCancelQuote,
  useCreateQuote,
  useCreateRevision,
  useDuplicateQuote,
  useFulfillmentOptions,
  useQuote,
  useQuoteNotifications,
  useQuoteRevisions,
  useRecordRejection,
  useSendQuote,
  type LineFulfillmentState,
  type QuoteDetail,
  type QuoteDuplicateDiff,
  type QuoteLine,
  type QuoteLineInput,
  type QuoteRejectionReason,
} from "../hooks/quote-queries";
import { useLoadedPager } from "../../../../lib/cursor-pages";
import { bookClientPager, bookPager } from "../hooks/book-pages";

/**
 * One row of the editable grid.
 *
 * `priceText` is DECIMAL MAJOR TEXT and empty means "let the engine resolve
 * it" — the distinction §5 draws with an absent `unit_price_minor`. An empty
 * string and a typed "0" are therefore different things, which is exactly why
 * the row keeps text rather than a number: a numeric field cannot express
 * "unset" without inventing a sentinel. A server line priced `manual` arrives
 * with it SEEDED (`rowsFromQuote`), so the negotiated figure is a value the
 * manager can keep, change or clear — not a placeholder the save forgets.
 *
 * `server` is the priced line the last save returned, when there is one. It is
 * what carries `price_source`, the margin block and the resolved list price —
 * facts only the server can produce. A locally-added row has none of them yet,
 * and the grid renders that as nothing rather than as zeros.
 */
type OrderLineInput = OrderCreateRequest["lines"][number];

/** The 1-based line a 422 PROMISE_DATE_IN_PAST names, or undefined for any other refusal. */
export function promiseRefusalLine(error: unknown): number | undefined {
  if (!(error instanceof RequestError) || error.code !== "PROMISE_DATE_IN_PAST") return undefined;
  const problem = (error.problem ?? {}) as Record<string, unknown>;
  const extensions = problem["extensions"] as Record<string, unknown> | undefined;
  const lineNo = problem["line_no"] ?? extensions?.["line_no"];
  return typeof lineNo === "number" ? lineNo : undefined;
}

interface GridRow {
  readonly key: string;
  readonly productId: string;
  readonly productLabel: string;
  readonly uomId: string;
  readonly qtyText: string;
  readonly priceText: string;
  readonly server: QuoteLine | undefined;
  /** The §3.3 / D-241 fulfilment read for this row, when it has been asked. */
  readonly fulfillment: LineFulfillmentState | undefined;
  /** The off-cut option the manager applied, as the §2.14 fulfilment plan. */
  readonly plan: QuoteLineInput["fulfillment_plan"];
  /**
   * What the SAVE put in the price box — `minorToDecimal(unit_price_minor)` for
   * a §2.14 `manual` line, `""` for every other source, `undefined` for a row
   * the document does not carry.
   *
   * It exists to tell "the manager typed over this line" apart from "the box is
   * showing the price the document already holds", which are the same thing to
   * a bare `priceText.length > 0` test and are NOT the same fact. See
   * `previewAsk`.
   */
  readonly serverPriceText: string | undefined;
  /** The promise date the seller typed for an order line, or "" (D-349 ruling 1). */
  readonly promiseDate: string;
  /**
   * The quoted promise Check date wrote on a quote line (D-465 ruling 5), and
   * its date. The save resends the id, and a line saved without it loses its
   * date; any edit to the quantity clears both, because the promise is for
   * exactly the quantity it was checked at.
   */
  readonly promiseId: string | undefined;
  readonly promiseHint: string | undefined;
  /**
   * Use in quote moved the line onto the checked unit and dropped the price
   * typed for the old one; the price cell says so until a price is typed.
   */
  readonly priceReset?: boolean;
}

/**
 * §3.2's answer for a line the document does not carry yet.
 *
 * §4.4's element list promises "resolved price with source chip" on the LINE
 * GRID, and until this shipped the grid could only show what a SAVE had
 * returned: a manager typing a product and a quantity saw an empty price cell
 * and no provenance until they committed. The promise was unqualified and the
 * annotation declared its other absences, so a reader took it at face value
 * (task #193). `POST /v1/pricing/resolve` is the same engine the §4.2 price
 * check already drives — a write-nothing dry run — so the fix is to ask it
 * rather than to invent a client-side price.
 *
 * FIVE STATES, because the absent one is not the same fact as the empty one and
 * a blank cell that means "still thinking" is the defect the floor tiles taught
 * (an unresolved thing must never look like a resolved one):
 *
 *   `pending`     — asked, no answer yet. Renders as the "Pricing…" chip.
 *   `value_hidden` — this reader is not shown prices (D-301). The dry run's
 *                   answer is prices, so the server refuses it 403 naming
 *                   `sales.read_value`. A reader the role mirror KNOWS lacks
 *                   the grant is never asked for, and the first refusal the
 *                   server sends a reader the mirror cannot judge stops the
 *                   asking for every row. Stated in the hint register, since
 *                   it is a fact about the role and not a failure.
 *   `priced`      — §3.2 answered. The price, the source chip, the off-list
 *                   flag and the margin block all read from it, under the same
 *                   §7 omission rule the saved rows honour: the margin block
 *                   renders only when the KEYS ARE PRESENT, and `margin_breached`
 *                   is always there for the reader who may not see the numbers.
 *   `not_listed`  — §9.13's 422. A FACT about the price book, not a failure of
 *                   this screen, so it wears the hint register and says what to
 *                   do: type a price.
 *   `failed`      — anything else. Said out loud rather than rendered as no
 *                   price, because "no price" is a claim this screen would be
 *                   making without evidence.
 */
type LinePreview =
  | { readonly kind: "pending" }
  | { readonly kind: "value_hidden" }
  | { readonly kind: "priced"; readonly result: PricingResolve }
  | { readonly kind: "not_listed" }
  | { readonly kind: "failed" };

/**
 * The inputs §3.2 prices from, as one string — the key BOTH the request cache
 * and the effect's change detection use.
 *
 * The customer is in it because tier, override and eligible-list membership all
 * hang off them (§3.2 steps 1-3), so the same product at the same quantity is a
 * different question for a different customer. `wanted_date` is NOT: the
 * composer resolves for TODAY, which is what `POST /v1/quotes` itself does
 * (`wantedDate = tenantToday(now)`), and a preview priced against a different
 * date than the save would use is worse than no preview.
 */
function previewAsk(
  row: GridRow,
  customerId: string,
  wantedDate: string,
  locale: string,
): PricingResolveRequest | undefined {
  // The quantity is read in the caller's locale for the same reason the price
  // is (D-274): `2,5` metres from a ru keyboard is a quantity, and a preview
  // that refuses it asks a different question than the save answers.
  const qty = normalizeTypedDecimal(row.qtyText.trim(), locale);
  if (customerId.length === 0) return undefined;
  if (row.productId.length === 0 || row.uomId.length === 0) return undefined;
  if (!QTY_TEXT.test(qty)) return undefined;
  // A saved line whose product, quantity and unit are all unchanged already
  // carries the server's own priced answer — asking again would spend a request
  // to be told what the row is already showing. A TYPED price is the exception:
  // §2.14 defines `off_list` against the RESOLVED price ("true when
  // unit_price_minor ≠ resolved engine price"), and the engine sets it nowhere
  // else (`resolve-engine.ts` step 6), so the only way to know whether a typed
  // figure is an edit at all is to ask what the engine would have resolved.
  // The typed price is deliberately NOT in the signature: the question is what
  // the engine resolves WITHOUT it.
  const server = row.server;
  // "STILL THE SAVED ANSWER" is a comparison against what the SAVE put here —
  // product, unit, quantity AND the price box's own text.
  //
  // THE DEFECT THIS FIXES (staging red on M09-S04, 2026-09-11). The old test
  // was `unchanged && !typed`, where `typed` meant "the price box is not
  // empty". But `rowsFromQuote` SEEDS that box from a §2.14 `manual` line's own
  // price — so merely REOPENING a quote with a hand-priced line read as "the
  // manager typed over it", the saved answer was discarded, and with it went
  // the `margin_breached` flag in the total column: the ONE margin fact §7
  // grants a reader who may not see the numbers. A breaching quote opened by a
  // sales manager without `sales.margin.read` showed no approval warning at
  // all, on a document nobody had touched.
  //
  // Comparing the TEXT (not a parsed figure) is deliberate: the seed is
  // canonical `minorToDecimal` output, so "the box holds what the save wrote"
  // is a string equality with no exponent to agree on. A manager who retypes
  // the same number in a different shape merely causes one extra dry run.
  const priceUnchanged = row.priceText.trim() === (row.serverPriceText ?? "").trim();
  const unchanged =
    server?.product_id === row.productId && server.uom_id === row.uomId && server.qty === qty;
  if (unchanged && priceUnchanged) return undefined;
  return {
    customer_id: customerId,
    product_id: row.productId,
    qty,
    uom_id: row.uomId,
    // THE APPLIED OFF-CUT PLAN TRAVELS WITH THE QUESTION. §2.14 prices an
    // off-cut line from its class, the save already sends this as
    // `fulfillmentHint`, and the dry run takes it — so leaving it out meant the
    // manager applied a discount in the panel ON THIS SCREEN and the preview
    // went on showing the undiscounted price. Higher than what the customer
    // would be charged, by exactly the percentage the panel had just shown them.
    ...(row.plan !== undefined ? { fulfillment_hint: row.plan } : {}),
    // THE TENANT'S today, which is the day the SAVE prices against
    // (`tenantToday(now, tenant.defaultTimezone)` in quotes.service). It used
    // to be the BROWSER's, so on a price-list activation boundary — in the
    // hours where the two calendars disagree — the preview quoted one day and
    // the save quoted another, on the one screen whose job is showing a
    // manager a number before they commit to it. D-089's rule, and this was
    // the last surface still breaking it. The zone rides the session feed the
    // role codes already come from, so it costs no request and cannot name a
    // different tenant than the permissions do.
    wanted_date: wantedDate,
  };
}

/**
 * The ask as ONE string — the identity both the staleness check and the
 * preview map use. Serialising the whole question (rather than a hand-joined
 * subset) is what lets the off-cut plan take part in it: a plan applied or
 * cleared is a different question, and the row must stop answering with the
 * old one.
 */
function previewSignature(
  row: GridRow,
  customerId: string,
  wantedDate: string,
  locale: string,
): string | undefined {
  const ask = previewAsk(row, customerId, wantedDate, locale);
  return ask === undefined ? undefined : JSON.stringify(ask);
}

function rowKey(): string {
  return crypto.randomUUID();
}

/**
 * Seed the grid from the server's revision.
 *
 * WHAT IS CARRIED, AND WHY THAT IS THE CLIENT'S JOB. A revision is a NEW,
 * immutable document priced from `input.lines` ALONE (§5's "full line payload,
 * never a patch"): the server never reads the previous revision's lines, and
 * `QuoteLineInput` has no "keep what it was" form — a line WITHOUT
 * `unit_price_minor` is repriced from the lists (§3.2), a line WITHOUT
 * `fulfillment_plan` is a line with none. So the two facts only the manager
 * produced — a negotiated `manual` price and an applied §3.3 off-cut
 * combination — reach revision n+1 only if this grid resends them, and it
 * resends exactly what it holds. Seeding them here is what makes "Save as a
 * new version" a restatement of the document rather than a silent repricing
 * of every line the manager did not touch. This file shipped without it: the
 * price cell showed the negotiated figure as a PLACEHOLDER over an empty
 * `priceText`, the `manual` chip stayed on, and the save sent
 * `{product, qty, uom}` alone — a customer-facing price changed by a
 * quantity edit on a different line.
 *
 * A `manual` line seeds `priceText` from the server's own `unit_price_minor`
 * through `minorToDecimal` — the exact inverse of the `decimalToMinor` the
 * payload applies, both string arithmetic at the same `digits`, so the round
 * trip is the identity. Every OTHER source leaves it EMPTY: those prices came
 * from a list, and §3.4's re-send "reprices at current lists" by design — an
 * engine-resolved figure resent as a manual one would freeze a list price and
 * flag it `off_list`. A non-empty plan seeds `plan`; the server's empty array
 * means "none" and stays `undefined`, which is the payload's absent form.
 *
 * A PRICE THE READER IS NOT SERVED SEEDS NOTHING (D-337 ruling 1). A reader
 * without `sales.read_value` gets every line with no `unit_price_minor`, and
 * `minorToDecimal(undefined)` is "N.aN", which `rowDefect` then refuses as an
 * unparseable price on a line nobody touched. The box is seeded empty instead,
 * and Save is held by `reviseValueHidden` below for the line whose empty box
 * would reprice a negotiated figure.
 */
function rowsFromQuote(quote: QuoteDetail, digits: number): GridRow[] {
  return quote.revision.lines.map((line) => {
    const seeded =
      line.price_source === "manual" && line.unit_price_minor !== undefined
        ? minorToDecimal(line.unit_price_minor, digits)
        : "";
    return {
      key: line.id,
      productId: line.product_id,
      productLabel: line.description,
      uomId: line.uom_id,
      qtyText: line.qty,
      priceText: seeded,
      server: line,
      fulfillment: undefined,
      plan: line.fulfillment_plan.length > 0 ? line.fulfillment_plan : undefined,
      serverPriceText: seeded,
      promiseDate: "",
      ...(line.promise_id !== null && line.promise_date_hint !== null
        ? { promiseId: line.promise_id, promiseHint: line.promise_date_hint }
        : { promiseId: undefined, promiseHint: undefined }),
    };
  });
}

/**
 * The saved line's price was withheld from this reader (D-337 ruling 1): the
 * key is absent, which §7 uses for "not shown to your role" and never for a
 * line without a price. Every saved line is priced, so absence has no other
 * reading.
 */
function priceWithheld(line: QuoteLine | undefined): boolean {
  return line !== undefined && line.unit_price_minor === undefined;
}

/**
 * §3.4's "can this document still be edited" — the one place that decides, so
 * no control asks the question a second way. Everything else edits, and
 * editing a `sent` or `approved` quote MINTS A NEW REVISION rather than
 * patching one.
 *
 * THE SET IS THE SERVER'S SET, `accepted` INCLUDED. §3.4's prose names three
 * terminals and this predicate copied them; `quotes/quote-state.ts` — the
 * authority every §5 route refuses through — carries FOUR, and states why:
 * `accepted` is the momentary state between the token claim and `converted`
 * inside the acceptance transaction, and a document being turned into an order
 * is not one a manager may revise or cancel out from under it. Reading the
 * shorter list here offered Save and Send on an accepted quote and let the
 * manager discover the 409 by pressing it.
 */
function isTerminal(status: string): boolean {
  return (
    status === "accepted" ||
    status === "converted" ||
    status === "rejected" ||
    status === "cancelled"
  );
}

function isRevisionEdit(status: string): boolean {
  return status === "sent" || status === "approved" || status === "expired";
}

/** §3.4's cancel row — draft, pending_approval, approved, sent — mirroring
 * `canCancel` in `quotes/quote-state.ts`. `expired` is absent from that row
 * and stays absent here: an expired offer has already stopped being one, and
 * the route answers 409 `QUOTE_TERMINAL` for it. */
function isCancellable(status: string): boolean {
  return (
    status === "draft" ||
    status === "pending_approval" ||
    status === "approved" ||
    status === "sent"
  );
}

/**
 * The send's refusal as a sentence. 422 DOCUMENT_GLYPH_COVERAGE (D-288 ruling
 * 9: the PDF is drawn inside the send, so a character the document fonts
 * cannot print stops it) names the characters with their U+ codes and says
 * where they live — the quote, or the document header or footer in the sales
 * settings, which no field on this screen shows. Anything else is the verb's
 * sentence; the note adds the server's own title beneath it.
 */
function sendFailureText(t: TFunction, error: unknown): string {
  const failed = t("sales.composer.sendFailed");
  if (!(error instanceof RequestError) || error.code !== "DOCUMENT_GLYPH_COVERAGE") return failed;
  const characters = glyphCharactersSentence(t, glyphCodePointsOf(error));
  return `${failed} ${characters} ${t("sales.composer.sendGlyphsSource")}`;
}

/**
 * The document KIND this component composes (P3-D PR-D1, D-283).
 *
 * `"quote"` — the §4.4 quote composer on `/sales/quotes/new` and
 * `/sales/quotes/$quoteId`: draft save, revision, send, the §3.4 lifecycle
 * verbs. `"order"` — the SAME component on `/sales/orders/new`: no validity,
 * no note to the customer, no internal note (`sales_orders` has no such
 * column), a facility picker and a customer-PO field, and ONE verb — the
 * press is `POST /v1/orders` and lands on the order, `confirmed` or
 * `pending_approval` as the server decides. A second component would be a
 * second grid, a second price table and a second chance for the two to
 * disagree about what a line is — the same reason "new" and "edit" share
 * this file. The write gate differs: `sales.order.write`, not
 * `sales.quote.write`, so a holder of one and not the other gets exactly the
 * editors their code admits.
 */
export type ComposerDocument = "quote" | "order";

export function SalesQuoteComposerScreen({
  document = "quote",
}: {
  readonly document?: ComposerDocument;
} = {}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const perms = useSalesPermissions();
  const params = useParams({ strict: false });
  // ONE derived flag for the order mode; every difference below reads it.
  const isOrder = document === "order";
  const quoteId = !isOrder && typeof params.quoteId === "string" ? params.quoteId : "";
  // An order is always composed NEW: there is no `/sales/orders/$orderId`
  // arm of this component (an order is read on order-detail.tsx and never
  // re-edited — §3.6 has no revision row).
  const isNew = quoteId.length === 0;
  const isDesktop = useDesktop();

  // The audience is the document's: §4.5's quote-book roles for a quote,
  // §4.7's (wider) order-book roles for an order — and `sales.read` for both.
  // A NEW order has no read-only form: there is no document yet, so a reader
  // without the write code would be shown a composer with no editors and no
  // Place — a control-less shell, the D-088 shape. Refuse it as access.
  const refused =
    isSalesReadRefused(
      perms,
      (isOrder ? perms.canOpenOrders : perms.canOpenQuotes) && perms.has("sales.read"),
    ) ||
    (isOrder && !perms.has("sales.order.write"));
  const detail = useQuote(refused || isNew ? "" : quoteId);
  const quote = detail.data;

  // The write gate is the code the document's own route carries —
  // `POST /v1/orders` checks `sales.order.write` (spec 09 §7, D-283), the
  // quote routes `sales.quote.write`. Send is a quote verb only: an order has
  // nothing to dispatch (the Confirmed push is the server's, on entry to
  // `confirmed`).
  const canWrite = isOrder ? perms.has("sales.order.write") : perms.has("sales.quote.write");
  const canSend = !isOrder && perms.has("sales.quote.send");

  // ---- the document's own fields -------------------------------------------
  const [customerId, setCustomerId] = useState("");
  const [customerText, setCustomerText] = useState("");
  const customerQ = useDebouncedValue(customerText.trim(), 250);
  const [contactId, setContactId] = useState("");
  const [validityDays, setValidityDays] = useState("14");
  const [noteToCustomer, setNoteToCustomer] = useState("");
  const [internalNote, setInternalNote] = useState("");
  // ORDER MODE's two header fields (P3-D PR-D1). `facilityId` "" means
  // "Choose automatically" — `fulfilling_facility_id` is OMITTED from the
  // body and the server runs the §2.16 derivation over the caller's
  // scope-filtered active pool. `poRef` is §2.16's `customer_po_ref`, the
  // customer's own PO number, trimmed and omitted when empty.
  const [facilityId, setFacilityId] = useState("");
  const [poRef, setPoRef] = useState("");
  // The row whose promise date was refused as before today (D-349 ruling 1).
  const [promiseRefused, setPromiseRefused] = useState<string | undefined>(undefined);
  const [rows, setRows] = useState<readonly GridRow[]>([]);
  const [dirty, setDirty] = useState(false);
  const [openOffcutKey, setOpenOffcutKey] = useState<string | undefined>(undefined);
  // Check date (D-465 ruling 5): the row whose CTP dialog is open, and the row
  // whose promise the last save refused, with the server's reason.
  const [checkKey, setCheckKey] = useState<string | undefined>(undefined);
  const [datedRefused, setDatedRefused] = useState<
    Readonly<Record<string, QuoteLinePromiseReason>>
  >({});
  const clearRefused = (key: string) => {
    setDatedRefused((prior) => {
      if (!(key in prior)) return prior;
      const { [key]: _gone, ...rest } = prior;
      return rest;
    });
  };
  // `planning.promise.commit`, read off the same role feed as the sales mirror.
  const canCheckDates = canRunCtp(perms);
  // The three §3.4 lifecycle panels, each closed until its verb is pressed:
  // cancel (mandatory reason), record-rejection (code + note) and the lazy
  // revision history. `undefined` reason_code is "not chosen yet" — the select
  // opens on the empty option rather than defaulting to `price`, because a
  // default lost-reason is a statistic nobody made.
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [rejectCode, setRejectCode] = useState("");
  const [rejectNote, setRejectNote] = useState("");
  const [versionsOpen, setVersionsOpen] = useState(false);

  // ACTIVE CUSTOMERS ONLY (task #229). §3.1.4 blocks every new document for an
  // archived customer, so `POST /v1/quotes` and `POST /v1/orders` answer 422
  // `CUSTOMER_ARCHIVED` for one — but `GET /v1/customers` applies its `status`
  // filter only when the caller SENDS it, and this picker sent `q` alone. An
  // archived name was therefore offered, selectable, priceable in the preview,
  // and refused at the save: a control that exists only to fail (D-118). The
  // filter is a query parameter the route has declared since P3-A and the §4.1
  // list screen already uses, so this costs no contract change and no server
  // change — it is a question the client simply never asked.
  //
  // The sales-assignment arm needs no client gate: `customers.service` forces
  // `assignedUserId` for a sales-narrowed caller, so this list is already the
  // caller's own book and the picker can only ever have surfaced the archive.
  const customerMatches = useCustomers(
    { q: customerQ, status: "active" },
    undefined,
    customerQ.length > 1,
  );

  // §4.4's one deep-link entry: `?customer_id=`, set by whoever linked here —
  // spec 17 §4.1's "Create quote" opens this screen from a conversation that
  // already knows whose it is, and the manager should not retype a name the
  // previous screen had in hand.
  //
  // It seeds the picker ONCE and for a NEW document only. A revision takes its
  // customer from the quote (the hydrate effect below), and letting a URL
  // param reach that would let a stale address rewrite an existing document's
  // customer — a quote silently addressed to somebody else is the worst thing
  // this screen could do.
  //
  // The id is committed only once it RESOLVES to a customer this caller may
  // read AND §3.1.4 still admits new documents for them. A forwarded link or a
  // seat whose assignments do not include them leaves the picker EMPTY and
  // typable rather than pointed at a name the reader cannot see: the request
  // the composer would then make is one the server refuses anyway.
  //
  // AN ARCHIVED CUSTOMER IS THE SAME OUTCOME BY A DIFFERENT ROUTE, and this
  // comment claimed it was already handled until task #229 measured it.
  // `GET /v1/customers/{id}` does NOT refuse an archived customer —
  // `requireVisible` carries an assignment arm and deliberately no status arm,
  // because §3.1.4 keeps archived customers resolvable — so spec 17 §4.1's
  // "Create quote" link, followed on a conversation whose customer has since
  // been archived, resolved cleanly and pre-filled the picker with a name
  // whose save then answered 422 `CUSTOMER_ARCHIVED`. The seed is now withheld
  // for that case as well, and the withholding is STATED beside the picker
  // (`sales-composer-customer-archived`) rather than left as an empty box the
  // manager reads as a broken link.
  const composerSearch: Record<string, unknown> = useSearch({ strict: false });
  const linkedCustomerId = validateSalesComposerSearch(composerSearch).customer_id;
  // P3-E2 PR E2-1 (spec 09 §4.4, D-312 ruling 4): a composer opened from a
  // deal (`?customer_id=…&opportunity_id=…`, the deal page's Create quote)
  // carries the deal into the create, for a NEW quote only and only for a
  // holder of `sales.opportunity.write` (a link is a deal write, and the
  // create refuses anyone else 403 before the quote is written). The route's
  // validator reads the id defensively: a malformed one is dropped, never
  // sent.
  const linkedDealId =
    isNew && !isOrder
      ? dealForCreate(perms, validateSalesComposerSearch(composerSearch).opportunity_id)
      : undefined;
  const prefill = useCustomer360(
    isNew && !refused && linkedCustomerId !== undefined ? linkedCustomerId : "",
  );
  // Set the moment the manager touches the picker as well as on the seed, so
  // a slow resolve cannot overwrite a customer they have already chosen.
  const customerSeeded = useRef(false);
  useEffect(() => {
    const seed = prefill.data?.customer;
    if (customerSeeded.current || seed === undefined) return;
    // Marked seeded BEFORE the archive arm returns: the link has had its one
    // chance either way, and a later render must not re-try it over a name the
    // manager has since typed.
    customerSeeded.current = true;
    if (seed.status === "archived") return;
    setCustomerId(seed.id);
    setCustomerText(seed.display_name);
  }, [prefill.data]);
  // The sentence that goes where the seeded name would have been. Shown only
  // while the picker is still empty — the moment the manager chooses somebody,
  // it is a fact about a link they have already worked around.
  const linkedCustomerArchived =
    isNew && prefill.data?.customer.status === "archived" && customerId.length === 0;

  const owners = useOwnerIndex(perms.isAdmin);
  // A member directory the browser holds offline stays pending for the whole
  // cut, so the owner reads as its short id until it answers, the way the
  // 360 and the peek read it (#293).
  const directoryOffline = useReadsHeldOffline();
  const ownersResolving = owners.isPending && !directoryOffline;
  const memberRef = useMemberRefAsk(ownersResolving);
  const uoms = useUoms();
  // ORDER MODE's facility pool — the tenant's ACTIVE facilities, the §4.7
  // list's own read. The picker is offered only when there is more than one
  // (module 06's rule: no select with nothing to choose); with one, a sentence
  // says the order is fulfilled from where its stock stands. Quote mode never
  // asks — a read nothing renders is a request nothing needed.
  const facilities = useSalesFacilities(isOrder && !refused);
  const facilityItems = facilities.data?.items ?? [];
  // §2.10's classes, for ONE job: naming the discount a line was priced at.
  // `GET /v1/offcut-discount-classes` is `sales.read` and unpaginated (a bounded
  // definition set, the module-05/07 catalog rule), so every persona that can
  // open this screen can read it and it costs one cached query.
  //
  // The alternative was the row's availability read — but that only exists
  // after the manager opens the off-cut panel, so a REOPENED saved off-cut line
  // had no percentage at all, and the one it did show was
  // `offcut_options[0]`'s: the first option the search happened to return,
  // which is the applied one only by coincidence.
  const offcutClasses = useOffcutClasses(!refused);
  // `SalesPct` decimal text ("30.00") on BOTH shapes — the class row and the
  // §3.3 option carry the same wire type, so the chip reads identically
  // whichever answered.
  //
  // A class served without `discount_pct` (D-337 ruling 6: the rate goes only
  // to a holder of `sales.read_value`) is left out of the map, so its line
  // wears the unnumbered "Off-cut discount" chip rather than a rate the reader
  // was not given.
  const offcutPctById = new Map<string, string>(
    (offcutClasses.data ?? []).flatMap((c) =>
      c.discount_pct === undefined ? [] : [[c.id, c.discount_pct] as const],
    ),
  );

  // ONE exponent for the whole screen, derived before the seed so the grid is
  // seeded and the payload assembled at the same `digits`: `rowsFromQuote`'s
  // `minorToDecimal` and `linePayload`'s `decimalToMinor` are inverses only
  // when they agree on it.
  //
  // BEFORE THE FIRST SAVE THE CURRENCY IS THE CUSTOMER'S (audit row 85). The
  // server takes a new quote's currency from the customer's
  // `default_currency` (§2.1), and that value is already on the picker's own
  // rows — so the literal "KZT" that used to stand here printed a USD
  // customer's preview in tenge until the round trip, on the one screen whose
  // job is showing a manager a number before they commit to it. The literal
  // survives only as the last resort for a quote with no customer picked yet,
  // where there is no currency to be right about.
  const pickedCustomer = (customerMatches.data?.items ?? []).find((c) => c.id === customerId);
  // A DEEP-LINKED customer (`?customer_id=`) is answered by the seed read
  // before the name search has run, and its currency is the same fact — so the
  // first preview on an arrived-by-link composer prices in the customer's own
  // currency rather than spending a round trip in tenge.
  const seededCustomer = prefill.data?.customer;
  const currency =
    quote?.revision.currency ??
    pickedCustomer?.default_currency ??
    (seededCustomer?.id === customerId ? seededCustomer.default_currency : undefined) ??
    "KZT";
  const digits = minorUnitDigits(currency, i18n.language);

  // ONE business date for the screen, in the TENANT's calendar. §3.2's dry run
  // and the §3.3 off-cut panel both price against it, and `POST /v1/quotes`
  // prices against the same zone (`tenantToday(now, tenant.defaultTimezone)`),
  // so the preview and the save can no longer land on different days at a
  // price-list activation boundary — D-089's rule, and this screen was the last
  // surface still deriving a business date from the browser's calendar.
  //
  // DECLARED HERE, above every reader. It used to sit beside the other
  // derived values two hundred lines down, which put it in the temporal dead
  // zone of the preview effect that reads it — a runtime `ReferenceError` that
  // `tsc` does not see, and the same trap `showMargin`/`marginSourceOf` set
  // earlier in this file.
  //
  // `undefined` while the session feed is in flight, which `facilityTodayIso`
  // answers with the browser calendar by its documented contract. That window
  // is self-correcting rather than silent: the date is PART OF the preview's
  // question, so the row re-asks and replaces its own answer the moment the
  // zone lands.
  const tenantTimezone = useTenantTimezone();
  const today = facilityTodayIso(Date.now(), tenantTimezone);

  // ---- §4.4's pre-save price: the grid asks §3.2 for what it is showing ----
  //
  // ONE effect over ALL rows rather than a hook per row: the grid renders
  // through `DataTable` columns, not per-row components, so there is no place
  // to put a per-row hook — and a single pass also lets the debounce and the
  // staleness guard be stated once. Keyed by the row's own key, so a row that
  // is removed takes its preview with it.
  const resolvePrice = useResolvePrice();
  // Keyed by ROW, but each entry remembers the SIGNATURE it answers. A row's
  // key survives an edit to its quantity, so an entry that did not also carry
  // the question would keep answering with the price of the quantity before the
  // edit — the stale-shown-confidently defect this whole surface exists to
  // remove, reintroduced one level down.
  const [previews, setPreviews] = useState<
    ReadonlyMap<string, { readonly signature: string; readonly state: LinePreview }>
  >(new Map());
  // The signatures the grid WANTS priced, as one string, so the effect below
  // re-runs when an input changes and not when an unrelated field does.
  const wanted = JSON.stringify(
    rows
      .map((row) => ({ key: row.key, ask: previewAsk(row, customerId, today, i18n.language) }))
      .filter(
        (entry): entry is { key: string; ask: PricingResolveRequest } => entry.ask !== undefined,
      ),
  );
  const wantedDebounced = useDebouncedValue(wanted, 400);
  // Every answer carries the generation it was asked in; a later keystroke
  // bumps it and the older answers are dropped rather than allowed to land on
  // top of a newer question. Without this the grid can settle on the price for
  // a quantity the manager has already typed past.
  const askGeneration = useRef(0);
  // The questions already ASKED, by signature — answered or still outstanding.
  // A ref rather than state: it must not itself cause a render, and it has to
  // survive the effect re-running.
  const asked = useRef(new Set<string>());
  const resolveLine = resolvePrice.mutateAsync;
  // D-301: the dry run answers prices, and a reader without
  // `sales.read_value` is refused it. The role mirror decides first
  // (`refused` only when every role is a system role it knows), and the
  // server's own refusal decides for a role the mirror cannot see into. Either
  // way the grid states it once per row and stops asking, where it used to
  // show "could not price" on every line and ask again on every edit.
  const [serverValueRefused, setServerValueRefused] = useState(false);
  const pricesHidden = valueSortVerdict(perms) === "refused" || serverValueRefused;
  useEffect(() => {
    // The QUESTIONS, whole — not a positional string to be re-parsed. Every
    // field the request needs travels with the ask, which is how the customer
    // can no longer come from the closure and how the off-cut plan gets to
    // travel at all.
    const asks = (JSON.parse(wantedDebounced) as { key: string; ask: PricingResolveRequest }[]).map(
      (entry) => ({ key: entry.key, ask: entry.ask, signature: JSON.stringify(entry.ask) }),
    );
    askGeneration.current += 1;
    const generation = askGeneration.current;
    // Rows that no longer want a price lose theirs in the same pass, so a
    // cleared quantity cannot leave yesterday's figure under it.
    setPreviews((prior) => {
      const next = new Map<string, { signature: string; state: LinePreview }>();
      for (const ask of asks) {
        const held = prior.get(ask.key);
        next.set(ask.key, {
          signature: ask.signature,
          state: held?.signature === ask.signature ? held.state : { kind: "pending" },
        });
      }
      return next;
    });
    // FORGET the questions no row is asking any more, so a signature that comes
    // back later (an undone edit) is asked afresh rather than left unanswered
    // for ever.
    const live = new Set(asks.map((a) => a.signature));
    for (const signature of asked.current) {
      if (!live.has(signature)) asked.current.delete(signature);
    }
    if (asks.length === 0) return;
    // A reader who is not shown prices is not asked for one (D-301).
    // `previewOf` answers `value_hidden` for every priceable row instead.
    if (pricesHidden) return;
    for (const ask of asks) {
      // ASK EACH QUESTION ONCE. The effect re-runs whenever ANY row changes, and
      // it used to fire a request for every priceable row each time — twenty
      // POSTs per settled keystroke batch on a twenty-line quote, against a
      // route that is NOT idempotency-exempt, so each one also wrote an
      // `idempotency_keys` row carrying the full response body. The rows that
      // did not change already hold their answer; re-asking spent a request to
      // be told what they were showing.
      if (asked.current.has(ask.signature)) continue;
      asked.current.add(ask.signature);
      // THE REQUEST IS THE QUESTION, verbatim. Reading any field out of the
      // closure is what filed the NEW customer's answer under the OLD
      // customer's question — `customerId` changes immediately while the ask
      // list lags 400 ms, and §3.2 steps 1-3 hang entirely off the customer, so
      // that was one buyer's negotiated rate rendered as another's.
      void resolveLine(ask.ask)
        .then((result) => {
          if (askGeneration.current !== generation) return;
          setPreviews((prior) =>
            new Map(prior).set(ask.key, {
              signature: ask.signature,
              state: { kind: "priced", result },
            }),
          );
        })
        .catch((err: unknown) => {
          // Before the generation check: the refusal is about the READER,
          // not the question, so an answer to a stale question still says
          // every later question will be refused too.
          if (isSalesValueRefusal(err)) {
            setServerValueRefused(true);
            return;
          }
          if (askGeneration.current !== generation) return;
          const notListed = err instanceof RequestError && err.code === "PRICE_NOT_LISTED";
          setPreviews((prior) =>
            new Map(prior).set(ask.key, {
              signature: ask.signature,
              state: { kind: notListed ? "not_listed" : "failed" },
            }),
          );
        });
    }
    // `customerId` is NOT a dependency: it is already inside `wanted`, and
    // depending on it fires this effect against a stale ask list.
  }, [wantedDebounced, resolveLine, pricesHidden]);

  // Seed the grid from the server's revision; never over local edits (the
  // price-list grid's rule, for the same reason — a refetch mid-typing must
  // not eat what the manager has entered).
  useEffect(() => {
    if (quote === undefined || dirty) return;
    setRows(rowsFromQuote(quote, digits));
    setValidityDays(String(quote.validity_days));
    setNoteToCustomer(quote.revision.note_to_customer ?? "");
    setInternalNote(quote.revision.internal_note ?? "");
    setCustomerId(quote.customer_id);
    setContactId(quote.contact_id ?? "");
  }, [quote, dirty, digits]);

  const create = useCreateQuote();
  // ORDER MODE's one verb (P3-D PR-D1): `POST /v1/orders`. The server prices
  // every line, decides the review set and answers `confirmed` or
  // `pending_approval`; the screen lands on the order either way.
  const createOrder = useCreateOrder();
  const revise = useCreateRevision(quoteId);
  const send = useSendQuote(quoteId);
  const duplicate = useDuplicateQuote(quoteId);
  const cancelQuote = useCancelQuote(quoteId);
  const recordRejection = useRecordRejection(quoteId);
  const fulfillment = useFulfillmentOptions();
  // §5's revision history, asked for only once the panel is open (D-241's
  // lazy rule) — the composer's first paint is the document, not its past.
  const revisions = useQuoteRevisions(quoteId, versionsOpen);
  // The §9.3 diff handed over by whichever document duplicated INTO this one.
  const arriving = useArrivingDuplicateDiff(quoteId);

  useEffect(() => {
    if (detail.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [detail.error, navigate]);

  if (refused) {
    return (
      <ForbiddenState
        title={t("sales.access.title")}
        body={t(isOrder ? "sales.access.orders" : "sales.access.quotes")}
      />
    );
  }
  if (!isNew && detail.isPending) {
    return <CardSkeleton lines={8} label={t("sales.common.loading")} />;
  }
  if (!isNew && (detail.error !== null || quote === undefined)) {
    return (
      <ErrorState
        title={t("sales.composer.error")}
        retryLabel={t("sales.common.retry")}
        requestIdLabel={t("sales.common.requestId")}
        requestId={requestIdOf(detail.error)}
        onRetry={() => {
          void detail.refetch();
        }}
      />
    );
  }

  const agentNote = revisionAgentNote(quote?.revision);
  const vatMode = quote?.revision.vat_mode ?? "exclusive";
  const vatRate = quote?.revision.vat_rate_pct ?? "0";
  // THE TAX LINE IS NOT KNOWN BEFORE THE FIRST SAVE (audit row 85). The tenant
  // VAT rate reaches this screen only ON a saved revision (`vat_rate_pct` is a
  // `QuoteRevision` field; no read on this screen carries the tenant setting),
  // so an unsaved draft used to print "VAT 0.00" and "VAT added on top" — a
  // stated rate of zero, which for a KazKabel quote is simply false and is the
  // one number on the preview a manager might repeat to a customer. Absence,
  // not a made-up zero: the subtotal is the honest half and the note under it
  // already says the server recalculates on save.
  const vatKnown = quote !== undefined;
  const terminal = quote !== undefined && isTerminal(quote.status);
  const revisionEdit = quote !== undefined && isRevisionEdit(quote.status);
  // THE SERVER'S TARGET-CUSTOMER VERDICT (task #229, D-118), which gates both
  // of this screen's document-minting doors. `POST /revisions` and
  // `POST /duplicate` each call `requireCustomer` FIRST, so an archived
  // customer — or one a sales rep owns a quote for but is no longer assigned
  // to — refuses every press of either. It was offered anyway: Save on any
  // non-terminal quote, Duplicate at any status at all, each answering 422
  // `CUSTOMER_ARCHIVED` or 404 `CUSTOMER_NOT_FOUND` with the server's wire
  // English printed into a Russian screen by the failure note.
  //
  // A NEW document has no quote to ask about and reads true: its own target
  // check is the picker's (`status: "active"` above) and the create's 422.
  const customerQuotable = quote === undefined || quoteCustomerQuotable(quote);
  const editable = canWrite && !terminal && isDesktop && customerQuotable;
  // D-268 (2026-09-10): `POST /send` answers 409 QUOTE_EXPIRED on an expired
  // quote, because §3.4's `expired → sent` row goes through revision n+1
  // repriced at current lists. So the screen offers the verb that works.
  const expired = quote?.status === "expired";
  // A `const` local so the narrowing survives into the banner's own callbacks
  // (a mutable property read inside a closure does not narrow).
  const duplicateDiff: QuoteDuplicateDiff | undefined = arriving.diff;
  // §3.4's cancel row, and its rejected row as the SELLER records it. Both are
  // gated on the code their routes carry; the "owner sales" half of §3.4's
  // actor column is a SERVICE narrowing (D-040's second layer) and is
  // deliberately not re-derived here — the client holds no user id to compare
  // owners with, so a client-side guess would either hide a verb an owner has
  // or offer one a colleague does not. The order screen's cancel makes the
  // same call.
  //
  // BOTH RIDE `isDesktop` as well, and that is §4.4's sentence rather than a
  // preference: "mobile: read + approve/send only". Cancel is also the verb
  // with the least recoverable consequence on this screen — it REVOKES the
  // customer's live link — which is exactly the class of press a phone should
  // not be able to make by accident. Offering them on a phone would need
  // §4.4's mobile line amended first.
  const canCancelQuote =
    canWrite && isDesktop && quote !== undefined && isCancellable(quote.status);
  // The server admits a rejection from any LIVE state; §3.4's own table draws
  // the transition from `sent`, which is the state a manager is in when a
  // customer says no to an offer they were sent.
  const canRecordRejection = canWrite && isDesktop && quote?.status === "sent";

  /** The unit price the grid displays for a row: the server's resolved price
   * when it has one, or the manager's typed text. */
  /** The preview state for the question this row is asking RIGHT NOW — an entry
   * left over from a previous question answers nothing. */
  const previewOf = (row: GridRow): LinePreview | undefined => {
    const signature = previewSignature(row, customerId, today, i18n.language);
    if (signature === undefined) return undefined;
    if (pricesHidden) return { kind: "value_hidden" };
    const held = previews.get(row.key);
    return held?.signature === signature ? held.state : { kind: "pending" };
  };

  /**
   * The SAVED line, but only while it still describes the row.
   *
   * A row is described by its own saved line when nothing about it has changed
   * — same product, quantity and unit, and no price typed over it. The moment
   * any of those moves, the saved figures are about a different line, and every
   * provenance cell must stop reading them together rather than one at a time.
   */
  const savedAnswerFor = (row: GridRow): QuoteLine | undefined =>
    previewSignature(row, customerId, today, i18n.language) === undefined ? row.server : undefined;

  /** The manager's typed price as integer minor units, when it parses. */
  const typedMinorOf = (row: GridRow): number | undefined =>
    row.priceText.trim().length > 0
      ? decimalToMinor(normalizeTypedDecimal(row.priceText, i18n.language), digits)
      : undefined;

  /**
   * §2.14's off-list flag for what the row is SHOWING.
   *
   * "True when `unit_price_minor` ≠ **resolved engine price**" — the resolved
   * price, never the list price. An override or an off-cut class resolves BELOW
   * the list price and is not off-list; comparing against the list painted the
   * red chip (and its approval implication) on every governed discount, which
   * the save then contradicted. `undefined` means NOT KNOWN — no chip — which is
   * the honest answer while the engine's resolved price is still outstanding.
   */
  const offListOf = (row: GridRow): boolean | undefined => {
    const saved = savedAnswerFor(row);
    if (saved !== undefined) return saved.off_list;
    const typed = typedMinorOf(row);
    if (typed === undefined) return false; // an engine-resolved price is the resolved price
    const priced = pricedPreview(row);
    return priced === undefined ? undefined : typed !== priced.unit_price_minor;
  };

  /**
   * The margin block's subject, or `undefined` when nobody can honestly state
   * one. §3.2 step 6 recomputes margin ON the manual price, and the dry run has
   * no manual field (`PricingResolveRequest`), so the engine's margin describes
   * a price the manager is NOT quoting. Showing it would be worse than showing
   * nothing: it is a number about a different price, in the column that decides
   * whether the quote needs approval.
   */
  const marginSourceOf = (row: GridRow): QuoteLine | PricingResolve | undefined => {
    const saved = savedAnswerFor(row);
    if (saved !== undefined) return saved;
    return typedMinorOf(row) === undefined ? pricedPreview(row) : undefined;
  };

  /**
   * The §3.2 answer this row is currently showing, when it is a priced one AND
   * it is denominated in the currency this screen is formatting with.
   *
   * The dry run answers in the CUSTOMER's current `default_currency`; an
   * existing quote is formatted in `quote.revision.currency`, pinned when it was
   * raised. `QUOTE_CURRENCY_STALE` exists because those two diverge — and when
   * they do, rendering the answer would print a USD figure labelled KZT, at
   * KZT's exponent. A price in the wrong currency is not a price.
   */
  const pricedPreview = (row: GridRow): PricingResolve | undefined => {
    const preview = previewOf(row);
    if (preview?.kind !== "priced") return undefined;
    return preview.result.currency === currency ? preview.result : undefined;
  };

  const displayUnitMinor = (row: GridRow): number | undefined => {
    const typed =
      row.priceText.trim().length > 0
        ? decimalToMinor(normalizeTypedDecimal(row.priceText, i18n.language), digits)
        : undefined;
    if (typed !== undefined) return typed;
    return savedAnswerFor(row)?.unit_price_minor ?? pricedPreview(row)?.unit_price_minor;
  };

  // The margin column exists iff the SERVER put the keys on at least one line
  // (§7 omission, the area's one rule) — on a SAVED line or on §3.2's dry run
  // for a row it has priced. Reading `row.server` alone meant a brand-new draft
  // never grew the column at all, so the per-cell preview below was unreachable
  // on the composer's primary flow and a privileged reader saw no margin until
  // the save. The §7 rule is untouched: `marginVisible` still decides, and it
  // decides on KEY PRESENCE.
  const showMargin = columnPresent(rows, (row) => {
    const source = marginSourceOf(row);
    return source !== undefined && marginVisible(source) ? source.margin_pct : undefined;
  });

  // A PRICEABLE row with no price yet is not a zero — it is an unknown, and
  // summing what is left renders a confident `KZT 0.00` for a document nobody
  // has priced. `unpriced` is that fact, and the totals block states it instead
  // of adding up a subset: the same rule the cells follow one element up.
  const priceableRows = rows.filter(
    (row) => row.productId.length > 0 && qtyTypedOk(row.qtyText, i18n.language),
  );
  const lineTotals = priceableRows
    .map((row) => {
      const unit = displayUnitMinor(row);
      if (unit === undefined) return undefined;
      return lineTotalMinor(row.qtyText, unit);
    })
    .filter((value): value is number => value !== undefined);
  const unpricedRows = priceableRows.length - lineTotals.length;

  // The server's own totals win the moment they exist AND nothing is dirty;
  // otherwise the preview answers. Which one is on the glass is SAID, not left
  // to be inferred — that is `totalsPreview`.
  const serverTotals =
    quote !== undefined && !dirty && quote.revision.total_minor !== undefined
      ? {
          subtotalMinor: quote.revision.subtotal_minor ?? 0,
          taxMinor: quote.revision.tax_minor ?? 0,
          totalMinor: quote.revision.total_minor,
        }
      : undefined;
  const totals = serverTotals ?? previewTotals(lineTotals, vatMode, Number(vatRate));
  // A SAVED revision with no total was served to a reader without
  // `sales.read_value` (D-301; its lines lost their prices too under D-337
  // ruling 1). The preview cannot stand in for it: every line's price is
  // absent, so the sum it would print is a confident "KZT 0.00" for a document
  // that is priced. One sentence takes the block's place instead.
  const totalsWithheld = quote !== undefined && quote.revision.total_minor === undefined;
  // SAVE IS HELD while any off-list line's price was withheld (D-337, §4.4).
  // A revision line with no `unit_price_minor` means "reprice from the lists"
  // (§3.4), and this reader's box for such a line is empty because the price
  // was never served to it. Pressing Save would replace a negotiated figure the
  // reader cannot see with the list price, silently, on a customer-facing
  // document. A line that is not off-list was priced at what the lists
  // resolved, so it reprices as it always would. Only a tenant's custom role
  // meets this: every system role that writes quotes holds the grant.
  const reviseValueHidden =
    !isNew && rows.some((row) => row.server?.off_list === true && priceWithheld(row.server));

  /**
   * What stops this row reaching the save, if anything.
   *
   * `linePayload` DROPS a row whose quantity does not parse, and omits
   * `unit_price_minor` when the typed price does not — so a manager who blanks a
   * quantity to retype it, or types `40 000,00` with the separator their own
   * keyboard and locale use, gets a line silently missing from a customer-facing
   * document, or a negotiated price silently replaced by the list rate. Neither
   * was reported, and §3.4 makes the result a new immutable revision.
   *
   * The wave-4 duplicate path already learned this and got a dropped-lines
   * banner; the SAVE path had nothing. It refuses now instead.
   */
  const rowDefect = (row: GridRow): "qty" | "price" | undefined => {
    if (row.productId.length === 0) return undefined; // not a line yet
    if (!qtyTypedOk(row.qtyText, i18n.language)) return "qty";
    if (
      row.priceText.trim().length > 0 &&
      decimalToMinor(normalizeTypedDecimal(row.priceText, i18n.language), digits) === undefined
    ) {
      return "price";
    }
    return undefined;
  };
  // WHICH LINES, AND WHY — not just how many (#204 / D-274). A count tells a
  // manager that something is wrong and leaves them to find it by eye on a
  // twenty-line quote; the defect is already known per row, so it is named per
  // row.
  //
  // NAMED BY PRODUCT, positioned by number. An adversarial review caught the
  // first cut claiming the 1-based index was "what the rows are labelled with
  // on screen" — it is not: `lineColumns` carries no `#` column, and on a phone
  // the grid collapses to cards where counting is harder still. So the row is
  // identified by the thing a manager actually scans for, its product, with the
  // position beside it; a row whose product is not chosen yet has no label to
  // print and falls back to the number alone, which still beats a count. This
  // also survives a reorder, which a bare index would not.
  const lineDefects = rows
    .map((row, index) => ({
      line: index + 1,
      product: row.productLabel.trim(),
      defect: rowDefect(row),
    }))
    .filter(
      (entry): entry is { line: number; product: string; defect: "qty" | "price" } =>
        entry.defect !== undefined,
    );
  const incompleteRows = lineDefects.length;

  const payloadRows = (): GridRow[] =>
    rows.filter((row) => row.productId.length > 0 && qtyTypedOk(row.qtyText, i18n.language));
  const linePayload = (): QuoteLineInput[] =>
    payloadRows().map((row) => {
      const typed =
        row.priceText.trim().length > 0
          ? decimalToMinor(normalizeTypedDecimal(row.priceText, i18n.language), digits)
          : undefined;
      return {
        product_id: row.productId,
        qty: row.qtyText.trim(),
        uom_id: row.uomId,
        ...(typed !== undefined ? { unit_price_minor: typed } : {}),
        ...(row.plan !== undefined ? { fulfillment_plan: row.plan } : {}),
        // A direct order's line takes no promise (`OrderLineInput` is strict);
        // only a quote line carries the one Check date wrote.
        ...(!isOrder && row.promiseId !== undefined ? { promise_id: row.promiseId } : {}),
      } satisfies QuoteLineInput;
    });

  // A row the grid is SHOWING but the payload would drop makes Save refuse, not
  // proceed quietly. The manager fixes the row in front of them instead of
  // finding the omission on a document the customer already has.
  const payloadValid =
    incompleteRows === 0 && linePayload().length > 0 && (isNew ? customerId.length > 0 : true);

  // The promises the save refused (422 QUOTE_LINE_PROMISE_INVALID) are read
  // back onto their lines, every one at once, so the manager sees which dates
  // went stale and why and fixes them in one pass.
  const onSaveError = (error: unknown) => {
    const refusals = quoteLinePromiseRefusal(error);
    if (refusals === undefined) return;
    const rowsNow = payloadRows();
    const next: Record<string, QuoteLinePromiseReason> = {};
    for (const refusal of refusals) {
      const key = rowsNow[refusal.lineNo - 1]?.key;
      if (key !== undefined) next[key] = refusal.reason;
    }
    setDatedRefused(next);
  };

  const onSave = () => {
    send.reset();
    setDatedRefused({});
    if (isNew) {
      create.mutate(
        {
          customer_id: customerId,
          ...(contactId.length > 0 ? { contact_id: contactId } : {}),
          ...(linkedDealId !== undefined ? { opportunity_id: linkedDealId } : {}),
          lines: linePayload(),
          validity_days: Number(validityDays),
          ...(noteToCustomer.length > 0 ? { note_to_customer: noteToCustomer } : {}),
          ...(internalNote.length > 0 ? { internal_note: internalNote } : {}),
        },
        {
          onError: onSaveError,
          onSuccess: (created) => {
            setDirty(false);
            void navigate({
              to: "/sales/quotes/$quoteId",
              params: { quoteId: created.id },
            });
          },
        },
      );
      return;
    }
    revise.mutate(
      {
        lines: linePayload(),
        validity_days: Number(validityDays),
        ...(contactId.length > 0 ? { contact_id: contactId } : {}),
        ...(noteToCustomer.length > 0 ? { note_to_customer: noteToCustomer } : {}),
        ...(internalNote.length > 0 ? { internal_note: internalNote } : {}),
      },
      {
        onError: onSaveError,
        onSuccess: () => {
          setDirty(false);
        },
      },
    );
  };

  const sendResult = send.data;
  const breached = rows.some((row) => row.server?.margin_breached === true);
  // THE SEND PATH'S OWN RULE, mirrored (quotes.service.ts's
  // `decidedForThisRevision`): a breach sends the document to review UNLESS
  // this revision has already been decided — approved, or already sent once.
  // `margin_breached` stays true on the LINES of an approved quote (it is an
  // immutable fact about the price), so reading it alone put "Send for
  // approval" and the approval hint on a document the server dispatches
  // straight to the customer. `pending_approval` is not in the exemption on
  // purpose: the server answers that press with the review card again, which
  // is exactly what the label promises.
  //
  // ORDER MODE reads the GRID, not a saved document — there is none. The
  // direct door's own review rule is `marginBreached || offList` per line
  // (direct-orders.service.ts, the quote send's rule), and both facts are on
  // the glass already: the dry run's `margin_breached` for a row the engine
  // priced, and `offListOf` for a typed price that differs from the resolved
  // one. A row still resolving answers neither and does not flip the label —
  // the server decides regardless; the label is the screen's best statement
  // of what the press will do, never a second authority.
  const needsReview = isOrder
    ? rows.some((row) => marginSourceOf(row)?.margin_breached === true || offListOf(row) === true)
    : breached &&
      !(
        quote !== undefined &&
        (quote.status === "approved" || quote.sent_revision_no === quote.current_revision_no)
      );

  /** ORDER MODE's lines: the grid's, each with the promise date its seller typed. */
  const orderLinePayload = (): OrderLineInput[] => {
    const lines = linePayload();
    return payloadRows().map((row, i) => {
      const line = lines[i];
      if (line === undefined) throw new Error("line payload and rows disagree");
      return row.promiseDate.length > 0 ? { ...line, promise_date: row.promiseDate } : line;
    });
  };

  /** ORDER MODE's press: `POST /v1/orders`, then land on the order. */
  const onPlace = () => {
    // A date before the tenant's today is refused here as the door would refuse it.
    const early = payloadRows().find(
      (row) => row.promiseDate.length > 0 && row.promiseDate < today,
    );
    if (early !== undefined) {
      setPromiseRefused(early.key);
      return;
    }
    setPromiseRefused(undefined);
    const trimmedPo = poRef.trim();
    createOrder.mutate(
      {
        customer_id: customerId,
        ...(contactId.length > 0 ? { contact_id: contactId } : {}),
        ...(trimmedPo.length > 0 ? { customer_po_ref: trimmedPo } : {}),
        // "" = "Choose automatically": the key is OMITTED, not sent null, so
        // the wire says nothing about a facility and the §2.16 derivation runs.
        ...(facilityId.length > 0 ? { fulfilling_facility_id: facilityId } : {}),
        lines: orderLinePayload(),
      },
      {
        onError: (error) => {
          const lineNo = promiseRefusalLine(error);
          if (lineNo !== undefined) setPromiseRefused(payloadRows()[lineNo - 1]?.key);
        },
        onSuccess: ({ order: placed, warning }) => {
          setDirty(false);
          // The 201's `X-Sales-Warning` (DUPLICATE_PO_REF, D-289 ruling 4) is
          // about the document this navigation lands on, and the header is
          // gone once the response is — so it travels with the navigation,
          // the duplicate diff's one-slot hand-over, BEFORE it happens.
          if (warning !== undefined) handOverOrderWarning(placed.id, warning);
          void navigate({
            to: "/sales/orders/$orderId",
            params: { orderId: placed.id },
          });
        },
      },
    );
  };

  const setRow = (key: string, patch: Partial<GridRow>) => {
    setRows((prior) => prior.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    setDirty(true);
  };

  // The grid's columns. Editable cells render inputs; read-only ones render
  // the same value as text, so a phone (where `editable` is false) shows the
  // document rather than a form it cannot use.
  const showDelivery =
    !isOrder && ((editable && canCheckDates) || rows.some((r) => r.promiseHint !== undefined));
  const lineColumns: DataTableColumn<GridRow>[] = [
    {
      key: "product",
      header: t("sales.composer.colProduct"),
      id: true,
      render: (row) => row.productLabel,
    },
    {
      key: "qty",
      header: t("sales.composer.colQty"),
      numeric: true,
      render: (row) => (
        <>
          {editable ? (
            <input
              className="num"
              value={row.qtyText}
              inputMode="decimal"
              aria-label={t("sales.composer.qty")}
              data-testid="sales-line-qty"
              onChange={(e) => {
                // A promise is for exactly the quantity it was checked at,
                // so an edit drops the date and Check date is offered again.
                setRow(row.key, {
                  qtyText: e.target.value,
                  ...(row.promiseId !== undefined
                    ? { promiseId: undefined, promiseHint: undefined }
                    : {}),
                });
                clearRefused(row.key);
              }}
            />
          ) : (
            <span className="num">{row.qtyText}</span>
          )}{" "}
          <RefLabel
            value={uoms.data?.items.find((u) => u.id === row.uomId)?.code}
            id={row.uomId}
            pending={uoms.isPending}
          />
        </>
      ),
    },
    ...(isOrder
      ? [
          {
            key: "promise_date",
            header: t("sales.composer.colPromise"),
            render: (row: GridRow) =>
              editable ? (
                <span className="sales-line-promise">
                  <input
                    type="date"
                    value={row.promiseDate}
                    min={today}
                    aria-label={t("sales.composer.promiseDate")}
                    aria-invalid={promiseRefused === row.key || undefined}
                    data-testid="sales-line-promise"
                    onChange={(e) => {
                      setRow(row.key, { promiseDate: e.target.value });
                      if (promiseRefused === row.key) setPromiseRefused(undefined);
                    }}
                  />
                  {promiseRefused === row.key ? (
                    <span
                      className="field-error"
                      role="alert"
                      data-testid="sales-line-promise-error"
                    >
                      {t("sales.composer.promisePast")}
                    </span>
                  ) : null}
                </span>
              ) : (
                <span>
                  {row.promiseDate.length > 0
                    ? formatBusinessDate(row.promiseDate, i18n.language)
                    : t("sales.composer.promiseNone")}
                </span>
              ),
          } satisfies DataTableColumn<GridRow>,
        ]
      : []),
    // QUOTE MODE's delivery date (spec 09 §4.4, D-465 ruling 5). Check date
    // opens module 03's CTP dialog for the line; Use in quote writes the
    // promise onto it, and the cell then reads "Delivery by {date}" with a
    // Quoted chip until the customer accepts. The column exists when the
    // reader may check a date here, or when a line already carries one.
    ...(showDelivery
      ? [
          {
            key: "delivery",
            header: t("sales.composer.colDelivery"),
            render: (row: GridRow) => (
              <DeliveryCell
                row={row}
                quoted={quote === undefined || !isAcceptedOrLater(quote.status)}
                refused={datedRefused[row.key]}
                canCheck={editable && canCheckDates}
                checkable={
                  row.productId.length > 0 &&
                  QTY_TEXT.test(normalizeTypedDecimal(row.qtyText.trim(), i18n.language))
                }
                onCheck={() => {
                  setCheckKey(row.key);
                }}
                onClear={() => {
                  setRow(row.key, { promiseId: undefined, promiseHint: undefined });
                  clearRefused(row.key);
                }}
              />
            ),
          } satisfies DataTableColumn<GridRow>,
        ]
      : []),
    {
      key: "unit_price",
      header: t("sales.composer.colUnitPrice"),
      numeric: true,
      render: (row) => {
        const unit = displayUnitMinor(row);
        const server = savedAnswerFor(row);
        const priced = pricedPreview(row);
        const preview = previewOf(row);
        // §3.2's answer for this row — the saved line's, else the dry run's.
        // What the line costs if the manager types nothing.
        const resolvedMinor = server?.unit_price_minor ?? priced?.unit_price_minor;
        return (
          <>
            {/* A saved line whose price this reader is not served is read-only
                on price (D-337 ruling 1): a box would invite a figure typed over
                one the reader cannot see, and the sentence below says why the
                cell has no number. */}
            {editable && !priceWithheld(server) ? (
              <input
                className="num"
                value={row.priceText}
                inputMode="decimal"
                // The invitation, ALWAYS. What the line would cost if the
                // manager types nothing is money, and money is rendered as
                // money — by the sibling span below, through `formatMinor`.
                //
                // This used to be the raw `minorToDecimal` figure, and it was
                // the one unformatted number in a money column: the struck LIST
                // price beside it was already localized, so the figure the
                // customer is NOT paying read as money and the figure they ARE
                // paying did not. The box takes a bare decimal; the span states
                // the money. Same split as customer-overrides.tsx's currency
                // fact.
                //
                // THE READ/WRITE GAP IS CLOSED (#204 / D-274). This comment
                // stood here twice saying it was not — first claiming a fix it
                // did not make, then, corrected, describing a box that accepted
                // only `^\d{1,15}(\.\d+)?$`. Both are now false, so it says
                // what is true instead of what was.
                //
                // `normalizeTypedDecimal` reads the box in the ACTIVE LOCALE
                // before `decimalToMinor` parses it: the locale's own grouping
                // and decimal separators, plus the keypad dot wherever the
                // locale does not group with dots. A manager who reads
                // «50 000,00 ₸» off the span, negotiates down and types
                // `49 000,00` is understood. The ambiguity that argued for
                // deferring it is real and is RESOLVED rather than guessed at:
                // `1.234` is 1234 in ru and 1.234 in en because the answer
                // comes from the locale, and anything not confidently
                // interpretable is refused outright rather than repaired — see
                // `lib/money.ts`, whose whole design turns on refusing
                // `45000,00` in en instead of reading it as 4 500 000.
                //
                // The placeholder stays a bare invitation and does NOT show a
                // localised example. Its job is the empty-field announcement
                // below, and a worked figure there would compete with the
                // `aria-describedby` span that carries the actual money.
                placeholder={t("sales.composer.priceTyped")}
                aria-label={t("sales.composer.colUnitPrice")}
                // A `placeholder` on an empty labelled field IS announced; an
                // unassociated sibling span is NOT. Moving the figure out of the
                // placeholder therefore took it away from screen-reader users
                // entirely — they would hear "Unit price, Type a price" on every
                // unpriced row and never learn what the line costs. Described-by
                // only while the span is actually on the page.
                aria-describedby={
                  resolvedMinor !== undefined && row.priceText.trim().length === 0
                    ? `price-resolved-${row.key}`
                    : undefined
                }
                data-testid="sales-line-price"
                onChange={(e) => {
                  setRow(row.key, { priceText: e.target.value, priceReset: false });
                }}
              />
            ) : unit === undefined ? null : (
              formatMinor(unit, currency, i18n.language)
            )}
            {/* Use in quote moved the line onto another unit, so the price
                typed for the old one was dropped (a price is per unit). */}
            {editable && row.priceReset === true ? (
              <span className="sales-applied" data-testid="sales-line-price-reset">
                {t("sales.composer.deliveryPriceReset")}
              </span>
            ) : null}
            {/* §4.4 promises the manager reads the resolved price BEFORE the
                save. An empty box with an invitation states that nothing was
                typed; it does not state what the line costs. This does, as
                localized money, and only while the manager has typed nothing —
                once they have, their own figure is the truth and this would be
                a second price in the same cell. */}
            {editable && row.priceText.trim().length === 0 && resolvedMinor !== undefined ? (
              <span
                className="sales-applied"
                id={`price-resolved-${row.key}`}
                data-testid="sales-line-price-resolved"
              >
                {t("sales.composer.priceResolved", {
                  price: formatMinor(resolvedMinor, currency, i18n.language),
                })}
              </span>
            ) : null}
            {/* The struck-through list price shows only when there IS one and
                it differs. `list_price_minor` is nullable on the wire: a line
                the engine could not price from any list (§9.13's
                PRICE_NOT_LISTED, then priced by hand) has no list price, and
                "list 0" would be a discount claim nobody made. */}
            {(() => {
              // The struck-through list price comes from whichever answer the
              // cell is showing — the saved line, or §3.2's own dry run.
              const listMinor = server?.list_price_minor ?? priced?.list_price_minor;
              if (listMinor == null || resolvedMinor == null || listMinor === resolvedMinor)
                return null;
              return (
                <span className="sales-provisional">
                  {t("sales.composer.listPrice", {
                    price: formatMinor(listMinor, currency, i18n.language),
                  })}
                </span>
              );
            })()}
            {/* The four §3.2 states a row can be in before it is saved. NONE of
                them is an empty cell: an unresolved line that looks like a
                priced one is the floor-tile defect (#115) in a money column. */}
            {preview?.kind === "pending" ? (
              <span className="sales-provisional" data-testid="sales-line-pricing">
                {t("sales.composer.resolving")}
              </span>
            ) : null}
            {preview?.kind === "not_listed" ? (
              <span className="sales-hint" data-testid="sales-line-not-listed">
                {t("sales.composer.notListed")}
              </span>
            ) : null}
            {preview?.kind === "failed" ? (
              <span className="sales-hint" data-testid="sales-line-price-failed">
                {t("sales.composer.resolveFailed")}
              </span>
            ) : null}
            {/* The same sentence for a SAVED line whose price the server
                withheld (D-337 ruling 1). Without it the cell was an empty box,
                or nothing at all on a phone, which reads as a line nobody
                priced. */}
            {preview?.kind === "value_hidden" || priceWithheld(server) ? (
              <span className="sales-hint" data-testid="sales-line-price-hidden">
                {t("sales.composer.priceHidden")}
              </span>
            ) : null}
          </>
        );
      },
    },
    {
      key: "price_source",
      header: t("sales.composer.colSource"),
      render: (row) => {
        const server = savedAnswerFor(row);
        const priced = pricedPreview(row);
        // A price the manager TYPED is the price being quoted, so `manual` is
        // what the chip says — ahead of the saved line's own source, which is
        // about the figure they just replaced.
        // A price that does not PARSE is not a manual price — it is not a price.
        // Claiming `manual` for `40 000,00` put a chip and an off-list flag on a
        // row carrying no number at all.
        const typed = typedMinorOf(row) !== undefined;
        const source = typed ? ("manual" as const) : (server?.price_source ?? priced?.price_source);
        if (source === undefined) {
          // Asked but unanswered: the chip says so. `null` here would read as
          // "no provenance", which is a different claim.
          return previewOf(row)?.kind === "pending" ? (
            <StatusChip variant="neutral" dot={false} data-testid="sales-price-source-pending">
              {t("sales.composer.resolving")}
            </StatusChip>
          ) : null;
        }
        // The class that ACTUALLY priced this line: the saved line's own column,
        // else the dry run's `price_source_ref` (resolve-engine.ts step 3 sets
        // it to the class id). A percentage is printed only when it belongs to
        // THAT class — never the first option on an unrelated search.
        // `offcut_class_id` is the engine's own name for it and reads plainly;
        // `price_source_ref` is the same uuid by a more general name (step 3
        // sets both together — resolve-engine.ts), kept as the fallback because
        // the response declares `offcut_class_id` optional and the ref never is.
        const appliedClassId =
          server?.offcut_class_id ??
          priced?.offcut_class_id ??
          (priced?.price_source === "offcut_class" ? priced.price_source_ref : null);
        const appliedPct = appliedClassId == null ? undefined : offcutPctById.get(appliedClassId);
        // Only the dry run carries it — a SAVED line has no `distributor_fallback`
        // column on the wire, so the chip is a pre-save fact and says nothing
        // once the document exists. Stated rather than faked.
        const fallback = !typed && priced?.distributor_fallback === true;
        const offList = offListOf(row);
        return (
          <>
            <StatusChip
              // §9.5's distributor fallback is a FACT about the eligible list,
              // never an error: the engine silently resolved the b2b price
              // because the product is not on this distributor's list. §4.4
              // names this chip for THIS screen and the composer never had it,
              // so a distributor quote read as a plain "Tier price" that
              // silently meant something else. The §4.2 panel has rendered it
              // since P3-A; this is the same fact on the screen that writes the
              // document.
              variant={fallback ? "warn" : priceSourceVariant(source)}
              dot={source === "manual"}
              data-testid="sales-price-source"
              data-source={source}
              data-fallback={fallback ? "true" : undefined}
              data-preview={server === undefined ? "true" : undefined}
            >
              {fallback
                ? t("sales.priceCheck.fallbackChip")
                : source === "offcut_class" && appliedPct !== undefined
                  ? t("sales.priceSource.offcut_class", { pct: appliedPct })
                  : source === "offcut_class"
                    ? t("sales.priceSource.offcutPlain")
                    : t(`sales.priceSource.${source}`)}
            </StatusChip>
            {offList === true ? (
              <StatusChip variant="bad" data-testid="sales-off-list-chip">
                {t("sales.priceSource.offList")}
              </StatusChip>
            ) : null}
          </>
        );
      },
    },
    {
      key: "atp",
      header: t("sales.composer.colAtp"),
      render: (row) => {
        // D-241's traffic-light. The band is the SERVER's verdict (module 06
        // owns availability); an unasked line renders nothing, and an
        // ASKED-but-failed one renders the colourless `unknown` — never green.
        const band: AtpBand | undefined = row.fulfillment?.light;
        return (
          <>
            {band === undefined ? null : (
              <StatusChip
                variant={atpChipVariant(band)}
                data-testid="sales-atp-light"
                data-band={band}
              >
                {t(`sales.atp.${band}`)}
              </StatusChip>
            )}
            {editable ? (
              <Button
                size="sm"
                data-testid="sales-line-offcut"
                busy={fulfillment.isPending && openOffcutKey === row.key}
                busyLabel={t("sales.composer.resolving")}
                onClick={() => {
                  setOpenOffcutKey(row.key);
                  fulfillment.mutate(
                    {
                      customer_id: customerId,
                      product_id: row.productId,
                      qty: row.qtyText.trim(),
                      uom_id: row.uomId,
                      wanted_date: today,
                    },
                    {
                      onSuccess: (state) => {
                        setRows((prior) =>
                          prior.map((r) => (r.key === row.key ? { ...r, fulfillment: state } : r)),
                        );
                      },
                    },
                  );
                }}
              >
                {t("sales.composer.offcutOpen")}
              </Button>
            ) : null}
          </>
        );
      },
    },
    // §7 OMISSION: the margin column EXISTS only when the server put the keys
    // on at least one line, and a rendered column's absent cell renders
    // NOTHING. Every reader still gets `margin_breached` as §4.4's flag, in
    // the total column below.
    ...(showMargin
      ? [
          {
            key: "margin",
            header: t("sales.composer.colMargin"),
            numeric: true,
            render: (row: GridRow) => {
              // §7 is unchanged: the block renders only when the KEYS are
              // present, so a reader without `sales.margin.read` sees nothing
              // here and the `margin_breached` flag in the total column instead.
              const source = marginSourceOf(row);
              if (source === undefined || !marginVisible(source)) return null;
              return (
                <span data-testid="sales-line-margin">
                  {t("sales.composer.marginPct", { pct: source.margin_pct })}
                  {source.margin_guard_pct !== undefined ? (
                    <span className="sales-provisional">
                      {t("sales.composer.marginGuard", { pct: source.margin_guard_pct })}
                    </span>
                  ) : null}
                </span>
              );
            },
          } satisfies DataTableColumn<GridRow>,
        ]
      : []),
    {
      key: "line_total",
      header: t("sales.composer.colTotal"),
      numeric: true,
      render: (row) => {
        const unit = displayUnitMinor(row);
        const total = unit === undefined ? undefined : lineTotalMinor(row.qtyText, unit);
        return (
          <>
            {total === undefined ? null : formatMinor(total, currency, i18n.language)}
            {/* §4.4's flag for EVERY reader, including one who may not see the
                numbers. It follows the row rather than the document: a line
                whose inputs have moved reads §3.2's dry run, so a manager who
                types a breaching price learns it here instead of at the save.
                The SEND button's own label still reads the saved document's
                breach (`breached` above) — Send acts on what was saved, and it
                is disabled while the grid is dirty anyway. */}
            {marginSourceOf(row)?.margin_breached === true ? (
              <StatusChip variant="warn" data-testid="sales-needs-approval">
                {t("sales.composer.needsApproval")}
              </StatusChip>
            ) : null}
          </>
        );
      },
    },
    ...(editable
      ? [
          {
            key: "actions",
            header: t("sales.composer.colActions"),
            actions: true,
            render: (row: GridRow) => (
              <Button
                size="sm"
                variant="danger"
                data-testid="sales-line-remove"
                onClick={() => {
                  setRows((prior) => prior.filter((r) => r.key !== row.key));
                  setDirty(true);
                }}
              >
                {t("sales.composer.removeLine")}
              </Button>
            ),
          } satisfies DataTableColumn<GridRow>,
        ]
      : []),
  ];

  return (
    <section className="sales-composer" data-testid="sales-quote-composer" data-document={document}>
      <PageHeader
        title={
          isOrder
            ? t("sales.composer.newOrderTitle")
            : isNew
              ? t("sales.composer.newTitle")
              : (quote?.quote_no ?? "")
        }
        crumbs={[
          isOrder
            ? { label: t("sales.orders.detail.crumbRoot"), to: "/sales/orders" }
            : { label: t("sales.composer.crumbRoot"), to: "/sales/quotes" },
        ]}
        actions={
          quote !== undefined && canWrite ? (
            <>
              {/* DUPLICATE IS A WRITE VERB, so it rides `isDesktop` with the
                  rest of them (§4.4's "mobile: read + approve/send only"). It
                  mints a document a phone could not then edit.

                  AND IT RIDES `customerQuotable` (task #229): the press sends
                  an EMPTY body, so the duplicate is always addressed to this
                  quote's own customer, and `draftFromBasket` meets
                  `requireCustomer` before it prices a single line. Offered
                  over an archived customer it was a button whose every press
                  was a refusal — at every status, including the terminal ones
                  where it is the only verb left. */}
              {isDesktop && customerQuotable ? (
                <Button
                  size="sm"
                  data-testid="sales-quote-duplicate"
                  busy={duplicate.isPending}
                  busyLabel={t("sales.composer.duplicating")}
                  onClick={() => {
                    duplicate.mutate(
                      {},
                      {
                        onSuccess: (result) => {
                          // THE DIFF TRAVELS WITH THE NAVIGATION. `repriced`
                          // and `dropped` are stated once, in this body, about
                          // a document that is about to replace this one on
                          // screen — and the product LABELS exist only here,
                          // on the source revision we are still holding.
                          handOverDuplicateDiff(result.quote_id, {
                            sourceNo: quote.quote_no,
                            repriced: result.repriced,
                            dropped: result.dropped,
                            labels: Object.fromEntries(
                              quote.revision.lines.map((l) => [l.line_no, l.description]),
                            ),
                          });
                          void navigate({
                            to: "/sales/quotes/$quoteId",
                            params: { quoteId: result.quote_id },
                          });
                        },
                      },
                    );
                  }}
                >
                  {t("sales.composer.duplicate")}
                </Button>
              ) : null}
              {canRecordRejection ? (
                <Button
                  size="sm"
                  data-testid="sales-quote-reject-open"
                  onClick={() => {
                    setRejecting(true);
                    setCancelling(false);
                  }}
                >
                  {t("sales.composer.recordRejection")}
                </Button>
              ) : null}
              {canCancelQuote ? (
                <Button
                  size="sm"
                  variant="danger"
                  data-testid="sales-quote-cancel-open"
                  onClick={() => {
                    setCancelling(true);
                    setRejecting(false);
                  }}
                >
                  {t("sales.composer.cancelQuote")}
                </Button>
              ) : null}
            </>
          ) : undefined
        }
      />

      {quote !== undefined ? (
        <div className="sales-360-header">
          <div className="sales-360-idline">
            <span>{quote.customer_display_name}</span>
            <StatusChip variant="neutral" dot={false}>
              {t(`sales.tier.${quote.customer_tier}`)}
            </StatusChip>
            <StatusChip
              variant={quoteStatusVariant(quote.status)}
              data-testid="sales-composer-status"
              data-status={quote.status}
            >
              {t(`sales.quoteStatus.${quote.status}`)}
            </StatusChip>
            {quote.origin === "agent" ? (
              <StatusChip variant="accent" dot={false} data-testid="sales-agent-origin">
                {t("sales.composer.agentOrigin")}
              </StatusChip>
            ) : null}
            {/* THE PDF THAT WENT OUT (P3-D PR-D4, D-288 rulings 1, 2): the
                document stored with the latest send, with the validity date
                that send minted. A plain link to the door, which 302s to the
                file under the session cookie (lib/documents.ts). Offered
                whenever that send stored a PDF (`pdf_available`, the one
                truth about the door — whatever the status, since a quote
                revised after a send reads `draft` and the customer still
                holds that PDF; a quote sent before the renderer existed has
                none, and the door would 409), and only to a `sales.read_value`
                holder — a priced PDF cannot be redacted. On a phone too:
                reading the offer is §4.4's "read". */}
            {canOpenQuotePdf(quote, (code) => perms.has(code)) ? (
              <a
                className="ds-lnk"
                href={quotePdfPath(quote.id)}
                target="_blank"
                rel="noreferrer"
                data-testid="sales-quote-pdf"
              >
                {t("sales.quotes.pdf")}
              </a>
            ) : null}
          </div>
          {/* D-560 ruling 3: the document's facts on one labelled line (the
              audit read "v1 Oct 28, 2026 Days left: 14" as one unlabelled run
              in three sizes). */}
          <p className="sales-facts-line" data-testid="sales-composer-facts">
            <span className="sales-fact">
              <span className="sales-hint">{t("sales.quotes.colRevisions")}</span>{" "}
              <span className="num">
                {t("sales.quotes.revision", { count: quote.current_revision_no })}
              </span>
            </span>
            {quote.valid_until !== null ? (
              <span
                className="sales-fact"
                data-testid="sales-composer-validity"
                data-band={validityBand(quote.valid_until, today)}
              >
                <span className="sales-hint">{t("sales.quotes.colValid")}</span>{" "}
                <span className="num">{formatBusinessDate(quote.valid_until, i18n.language)}</span>
                {(() => {
                  const left = daysRemaining(quote.valid_until, today);
                  return left !== undefined && left >= 0 ? (
                    <>
                      {" "}
                      <span className="sales-provisional">
                        {t("sales.quotes.validDays", { count: left })}
                      </span>
                    </>
                  ) : null;
                })()}
              </span>
            ) : null}
          </p>
          {/* HOW THIS DOCUMENT ENDED, read back on the document itself (audit
              row 31). §2.12 keeps one `rejected_reason` column and §3.4 has two
              doors into it — the manager's own "record rejection" and cancel —
              and neither could be read back anywhere in the product: a manager
              typed what the customer said, pressed Record it, and the sentence
              vanished. The same blindness on the other side: an ACCEPTED quote
              carries `converted_order_no`, and the order it became was reachable
              only by searching the order book for it.

              The reason is rendered per door, because the two write the column
              differently — `rejectedReasonParts` in lib/quotes says how, and
              why a cancellation's words are never split. */}

          {/* E2-1 (spec 09 §4.4, D-312 ruling 4): the deal this quote
              belongs to, with Link and Unlink for a deal writer. Quote mode
              only: an order carries no deal. */}
          {!isOrder ? <QuoteDealControl quote={quote} desktop={isDesktop} /> : null}

          {/* D-241's collaborator surface, READ-ONLY here: assignments are a
              property of the CUSTOMER (§2.2), so they are edited on the 360
              and merely disclosed on the document they govern.

              THE NAMES GO THROUGH `RefLabel` (audit row 86). `useOwnerIndex` is
              disabled for a non-admin — `GET /v1/members` is gated on
              identity.view_members — so `byId` is empty for the sales manager
              this screen is built for, and the fallback printed a raw 36-char
              uuid mid-sentence. The register's answer is the short-id chip:
              "an identifier, never a broken name" (customers.tsx). The label is
              a NODE, so the sentence is composed here rather than interpolated
              into one i18n string. */}
          <p className="sales-facts-line" data-testid="sales-composer-collaborators">
            <span className="sales-fact">
              <span className="sales-hint">{t("sales.composer.collaboratorsOwner")}</span>{" "}
              <RefLabel
                value={owners.byId.get(quote.owner_user_id)}
                id={quote.owner_user_id}
                pending={ownersResolving}
                {...memberRef}
              />
            </span>
            {quote.collaborator_user_ids.length > 0 ? (
              <span className="sales-fact">
                <span className="sales-hint">{t("sales.composer.collaboratorsWithLabel")}</span>{" "}
                {quote.collaborator_user_ids.map((id, i) => (
                  <span key={id}>
                    {i > 0 ? ", " : ""}
                    <RefLabel
                      value={owners.byId.get(id)}
                      id={id}
                      pending={ownersResolving}
                      {...memberRef}
                    />
                  </span>
                ))}
              </span>
            ) : null}
            {/* One box, never split across two lines: a link that wraps has a
                bounding box as wide as both lines, so at 412 px it sat on top
                of the owner's name (W-SWEEP's overlap rule, lane 2). */}
            <a
              className="sales-collab-manage"
              href={`/sales/customers/${quote.customer_id}`}
              data-testid="sales-composer-collaborators-manage"
            >
              {t("sales.composer.collaboratorsManage")}
            </a>
          </p>
        </div>
      ) : null}

      {/* D-560 ruling 3: why this document cannot be edited, in one banner
          (the audit counted four separate notices): the terminal state with
          the customer's or the manager's reason or the order it became, and
          on a phone that line editing needs a bigger screen. */}
      {terminal || !isDesktop ? (
        <Banner
          kind="neutral"
          className="sales-composer-locked"
          data-testid="sales-composer-locked"
        >
          {terminal ? (
            <span className="sales-locked-line" data-testid="sales-composer-readonly">
              {t("sales.composer.readOnly")}
            </span>
          ) : null}
          {quote !== undefined &&
          quote.rejected_reason !== null &&
          (quote.status === "rejected" || quote.status === "cancelled")
            ? (() => {
                const parts = rejectedReasonParts(quote.status, quote.rejected_reason);
                if (parts.codeKey === undefined && parts.note === undefined) return null;
                return (
                  <span className="sales-locked-line" data-testid="sales-composer-rejected-reason">
                    {t(
                      quote.status === "cancelled"
                        ? "sales.composer.cancelledBecause"
                        : "sales.composer.rejectedBecause",
                    )}{" "}
                    {parts.codeKey !== undefined ? (
                      <StatusChip
                        variant="bad"
                        dot={false}
                        data-testid="sales-composer-reject-code"
                      >
                        {t(parts.codeKey)}
                      </StatusChip>
                    ) : null}
                    {parts.note !== undefined ? (
                      <span data-testid="sales-composer-reject-note">
                        {parts.codeKey !== undefined ? " " : ""}
                        {parts.note}
                      </span>
                    ) : null}
                  </span>
                );
              })()
            : null}
          {/* The order this quote became. `converted_order_id` is the id §5
              hands back on accept, so the link is the document's own answer to
              "and then what happened". */}
          {typeof quote?.converted_order_id === "string" &&
          typeof quote.converted_order_no === "string" ? (
            <span className="sales-locked-line" data-testid="sales-composer-converted">
              {t("sales.composer.convertedTo")}{" "}
              <a
                href={`/sales/orders/${quote.converted_order_id}`}
                data-testid="sales-composer-converted-link"
              >
                {quote.converted_order_no}
              </a>
            </span>
          ) : null}
          {!isDesktop ? (
            <span className="sales-locked-line" data-testid="sales-composer-mobile-note">
              {/* §4.4's mobile line, per document: a quote can still be read,
                  approved and sent from a phone; an order being composed has no
                  phone verb at all, and the note must not promise one. */}
              {t(isOrder ? "sales.composer.orderMobileNote" : "sales.composer.mobileNote")}
            </span>
          ) : null}
        </Banner>
      ) : null}

      {/* §9.3's DIFF, on arrival. It is dismissible and it names the lines by
          number and product, because "3 lines repriced" a manager cannot
          locate is a sentence they will ignore twice and then stop reading.
          The empty case is stated too — a duplicate where nothing moved is the
          normal one, and a positive "every line kept its price" is what makes
          the banner's silence meaningful the day it is NOT empty. */}
      {duplicateDiff !== undefined ? (
        <Card
          data-testid="sales-duplicate-diff"
          title={t("sales.composer.diffTitle", { no: duplicateDiff.sourceNo })}
          action={
            <Button size="sm" data-testid="sales-duplicate-diff-dismiss" onClick={arriving.dismiss}>
              {t("sales.common.close")}
            </Button>
          }
        >
          {duplicateDiff.repriced.length === 0 && duplicateDiff.dropped.length === 0 ? (
            <p data-testid="sales-duplicate-diff-none">{t("sales.composer.diffNone")}</p>
          ) : (
            <p data-testid="sales-duplicate-diff-counts">
              {t("sales.composer.diffRepriced", { count: duplicateDiff.repriced.length })}{" "}
              {t("sales.composer.diffDropped", { count: duplicateDiff.dropped.length })}
            </p>
          )}
          {duplicateDiff.dropped.length > 0 ? (
            <ul className="sales-review-lines">
              {duplicateDiff.dropped.map((line) => (
                <li key={line.line_no} data-testid="sales-duplicate-dropped-line">
                  {t("sales.composer.diffDroppedLine", {
                    line: line.line_no,
                    product: duplicateDiff.labels[line.line_no] ?? line.product_id,
                  })}
                </li>
              ))}
            </ul>
          ) : null}
          {duplicateDiff.dropped.length > 0 ? (
            <p className="sales-hint">{t("sales.composer.diffDroppedWhy")}</p>
          ) : null}
        </Card>
      ) : null}

      {/* §5's revision history — collapsed, and offered only once there IS a
          past (revision 1 has none). The read is lazy: `useQuoteRevisions` is
          enabled by the toggle, so opening a quote costs one request, not two. */}
      {quote !== undefined && quote.current_revision_no > 1 ? (
        <Card
          className="sales-360-card"
          data-testid="sales-quote-versions"
          title={t("sales.composer.versionsTitle")}
          count={quote.current_revision_no}
          action={
            <Button
              size="sm"
              data-testid="sales-quote-versions-toggle"
              aria-expanded={versionsOpen}
              onClick={() => {
                setVersionsOpen((open) => !open);
              }}
            >
              {versionsOpen ? t("sales.composer.versionsHide") : t("sales.composer.versionsShow")}
            </Button>
          }
        >
          {!versionsOpen ? (
            <p className="sales-hint">{t("sales.composer.versionsBody")}</p>
          ) : revisions.isPending ? (
            <CardSkeleton lines={3} label={t("sales.common.loading")} />
          ) : revisions.error !== null ? (
            <ErrorState
              title={t("sales.composer.versionsError")}
              retryLabel={t("sales.common.retry")}
              requestIdLabel={t("sales.common.requestId")}
              requestId={requestIdOf(revisions.error)}
              onRetry={() => {
                void revisions.refetch();
              }}
            />
          ) : (
            <ul className="sales-timeline" data-testid="sales-quote-version-list">
              {revisions.data.map((rev) => (
                <li key={rev.id} data-testid="sales-quote-version" data-revision={rev.revision_no}>
                  <span className="num">
                    {t("sales.quotes.revision", { count: rev.revision_no })}
                  </span>{" "}
                  <span className="num">{formatTimestamp(rev.created_at, i18n.language)}</span>{" "}
                  <span>{t("sales.composer.versionLines", { count: rev.lines.length })}</span>
                  {/* §7 OMISSION: the total is absent for a reader without
                      `sales.read_value`, and an absent total renders NOTHING —
                      never a zero, which would read as a free quote. */}
                  {rev.total_minor !== undefined ? (
                    <span className="num" data-testid="sales-quote-version-total">
                      {formatMinor(rev.total_minor, rev.currency, i18n.language)}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </Card>
      ) : null}

      {quote?.origin === "agent" ? (
        <Card
          className="sales-agent-banner"
          data-testid="sales-agent-banner"
          title={t("sales.composer.agentTitle")}
        >
          <p>{t("sales.composer.agentBody")}</p>
          {quote.source_message_text !== undefined ? (
            <blockquote className="sales-agent-source" data-testid="sales-agent-source">
              <p className="sales-hint">{t("sales.composer.agentSource")}</p>
              <p>{quote.source_message_text}</p>
            </blockquote>
          ) : (
            // Absent because there is no message, or because this reader may
            // not read module 17's conversations. Either way the banner says
            // so rather than rendering an empty quotation.
            <p className="sales-hint" data-testid="sales-agent-no-source">
              {t("sales.composer.agentNoSource")}
            </p>
          )}
          {agentNote !== undefined ? (
            <details className="sales-agent-note" data-testid="sales-agent-note">
              <summary>{t("sales.composer.agentNote")}</summary>
              <UntrustedText
                label={t("sales.composer.agentNote")}
                frameNote={t("sales.composer.agentNoteFrame")}
                text={agentNote.text}
                lang={agentNote.locale}
                data-testid="sales-agent-note-text"
              />
            </details>
          ) : null}
        </Card>
      ) : null}

      {/* §3.4's rejected row, as the SELLER records it: "the customer said no
          on the phone". The REASON CODE is the point — §2.12's lost-reason
          vocabulary is what P3-E's loss analysis counts, and a free note typed
          instead of a code is a sentence no report can read. The note rides
          beside it and is optional, which is why only the code gates submit.
          The server admits this from any live state; §3.4's own table draws
          the transition from `sent`, and that is the state a manager is
          actually in when a customer declines. */}
      {rejecting && canRecordRejection ? (
        <div className="sales-toolbar" data-testid="sales-quote-reject-block">
          <Field label={t("sales.composer.rejectReason")}>
            <Select
              value={rejectCode}
              data-testid="sales-quote-reject-code"
              onChange={(e) => {
                setRejectCode(e.target.value);
              }}
            >
              <option value="">{t("sales.composer.rejectReasonRequired")}</option>
              <option value="price">{t("sales.composer.rejectReasonPrice")}</option>
              <option value="lead_time">{t("sales.composer.rejectReasonLeadTime")}</option>
              <option value="stock">{t("sales.composer.rejectReasonStock")}</option>
              <option value="other">{t("sales.composer.rejectReasonOther")}</option>
            </Select>
          </Field>
          <Field label={t("sales.composer.rejectNote")} wide>
            <input
              value={rejectNote}
              placeholder={t("sales.composer.rejectNoteHint")}
              data-testid="sales-quote-reject-note"
              onChange={(e) => {
                setRejectNote(e.target.value);
              }}
            />
          </Field>
          <Button
            variant="danger"
            size="sm"
            disabled={rejectCode.length === 0}
            busy={recordRejection.isPending}
            busyLabel={t("sales.composer.rejectRecording")}
            data-testid="sales-quote-reject-confirm"
            onClick={() => {
              recordRejection.mutate(
                {
                  reason_code: rejectCode as QuoteRejectionReason,
                  ...(rejectNote.trim().length > 0 ? { note: rejectNote.trim() } : {}),
                },
                {
                  onSuccess: () => {
                    setRejecting(false);
                    setRejectCode("");
                    setRejectNote("");
                  },
                },
              );
            }}
          >
            {t("sales.composer.rejectConfirm")}
          </Button>
          <Button
            size="sm"
            data-testid="sales-quote-reject-keep"
            onClick={() => {
              setRejecting(false);
            }}
          >
            {t("sales.composer.rejectKeep")}
          </Button>
        </div>
      ) : null}

      {/* §3.4's explicit cancel. The reason is MANDATORY on the wire
          (`QuoteCancelRequest.reason` is `min(1)`), so the control refuses an
          empty one here rather than letting the server say it — and the hint
          says what the press does to the customer's live link, which is the
          part that cannot be undone. */}
      {cancelling && canCancelQuote ? (
        <div className="sales-toolbar" data-testid="sales-quote-cancel-block">
          <Field label={t("sales.composer.cancelReason")} wide>
            <input
              value={cancelReason}
              placeholder={t("sales.composer.cancelReasonHint")}
              data-testid="sales-quote-cancel-reason"
              onChange={(e) => {
                setCancelReason(e.target.value);
              }}
            />
          </Field>
          <Button
            variant="danger"
            size="sm"
            disabled={cancelReason.trim().length === 0}
            busy={cancelQuote.isPending}
            busyLabel={t("sales.composer.cancelling")}
            data-testid="sales-quote-cancel-confirm"
            onClick={() => {
              cancelQuote.mutate(cancelReason.trim(), {
                onSuccess: () => {
                  setCancelling(false);
                  setCancelReason("");
                },
              });
            }}
          >
            {t("sales.composer.cancelConfirm")}
          </Button>
          <Button
            size="sm"
            data-testid="sales-quote-cancel-keep"
            onClick={() => {
              setCancelling(false);
            }}
          >
            {t("sales.composer.cancelKeep")}
          </Button>
          <p className="sales-hint">{t("sales.composer.cancelWarning")}</p>
        </div>
      ) : null}

      {/* The `?customer_id=` link pointed at a customer §3.1.4 has closed
          (task #229). The seed is withheld — a name the save would refuse is
          worse than an empty picker — and the withholding is said out loud, so
          a manager arriving from spec 17 §4.1's "Create quote" reads a reason
          rather than a blank box they take for a broken link. */}
      {linkedCustomerArchived ? (
        <p className="sales-hint" data-testid="sales-composer-customer-archived">
          {t("sales.composer.linkedCustomerArchived")}
        </p>
      ) : null}

      {/* E2-1: the deal this new quote will belong to (spec 09 §4.4). */}
      {linkedDealId !== undefined ? <NewQuoteDealLine opportunityId={linkedDealId} /> : null}

      {isNew ? (
        <div className="filter-bar" data-testid="sales-composer-customer">
          <Field label={t("sales.composer.customer")} wide>
            <input
              value={customerText}
              placeholder={t("sales.composer.customerHint")}
              data-testid="composer-customer-q"
              onChange={(e) => {
                customerSeeded.current = true;
                setCustomerText(e.target.value);
                setCustomerId("");
              }}
            />
          </Field>
          {customerQ.length > 1 ? (
            <Field label={t("sales.composer.customer")}>
              <Select
                value={customerId}
                data-testid="composer-customer"
                onChange={(e) => {
                  customerSeeded.current = true;
                  setCustomerId(e.target.value);
                }}
              >
                <option value="">{t("sales.composer.customerRequired")}</option>
                {/* §4.4 asks for the TIER in the picker, because that is where
                    it decides which price list the manager is about to quote
                    from — and the picker did not show it. The reliability score
                    carried `reliabilityBand`'s raw variant token instead
                    ("ok"/"warn"/"bad"): a wire word, rendered as user-facing
                    English, in a Russian UI. §3.9 also requires every surface
                    showing a score to mark a PROVISIONAL one, which the
                    customer list and the 360 both do and this did not. An
                    `<option>` holds text and no chip, so the facts are
                    composed into the label rather than dropped. */}
                {(customerMatches.data?.items ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {[
                      c.display_name,
                      t(`sales.tier.${c.tier}`),
                      c.reliability_score === null
                        ? undefined
                        : c.reliability_provisional
                          ? `${String(c.reliability_score)} (${t("sales.customers.provisional")})`
                          : String(c.reliability_score),
                    ]
                      .filter((part): part is string => part !== undefined)
                      .join(" · ")}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
        </div>
      ) : null}

      <h2>{t("sales.composer.linesTitle")}</h2>

      <DataTable
        clientPager={bookClientPager(t, i18n.language, { appendable: true })}
        data-testid="sales-line-grid"
        columns={lineColumns}
        rows={rows}
        rowKey={(row) => row.key}
        empty={
          <EmptyState
            title={t("sales.composer.emptyLinesTitle")}
            body={t("sales.composer.emptyLinesBody")}
          />
        }
        caption={t("sales.composer.linesTitle")}
        rowTestId="sales-line-row"
        rowProps={(row) => ({ "data-product": row.productId })}
      />

      {/* §3.3's off-cut suggestion panel — INLINE, under the grid, for the row
          it was opened from: the option names physical bobbins ("43 m + 57 m,
          zone WH-C/B2") and a manager applying it needs the row it belongs to
          on screen at the same time. */}
      {openOffcutKey !== undefined && editable ? (
        <OffcutPanel
          row={rows.find((r) => r.key === openOffcutKey)}
          currency={currency}
          onClose={() => {
            setOpenOffcutKey(undefined);
          }}
          onApply={(plan) => {
            setRows((prior) => prior.map((r) => (r.key === openOffcutKey ? { ...r, plan } : r)));
            setDirty(true);
            setOpenOffcutKey(undefined);
          }}
        />
      ) : null}

      {checkKey !== undefined && editable && canCheckDates ? (
        <CheckDateDialog
          row={rows.find((r) => r.key === checkKey)}
          quoteRef={quote?.quote_no}
          onUse={(quoted, baseUomId) => {
            const row = rows.find((r) => r.key === checkKey);
            if (row !== undefined) setRow(row.key, linePatchFromQuote(row, quoted, baseUomId));
            clearRefused(checkKey);
            setCheckKey(undefined);
          }}
          onClose={() => {
            setCheckKey(undefined);
          }}
        />
      ) : null}

      {editable ? (
        <AddLineRow
          onAdd={(row) => {
            setRows((prior) => [...prior, row]);
            setDirty(true);
          }}
        />
      ) : null}

      {totalsWithheld ? (
        <p className="sales-hint" data-testid="sales-totals-hidden">
          {t("sales.composer.totalsHidden")}
        </p>
      ) : (
        <dl className="sales-totals" data-testid="sales-composer-totals">
          <div>
            <dt>{t("sales.composer.subtotal")}</dt>
            <dd className="num">{formatMinor(totals.subtotalMinor, currency, i18n.language)}</dd>
          </div>
          {vatKnown ? (
            <div data-testid="sales-composer-tax-line">
              <dt>{t("sales.composer.tax")}</dt>
              <dd className="num">{formatMinor(totals.taxMinor, currency, i18n.language)}</dd>
            </div>
          ) : null}
          {vatKnown ? (
            <div>
              <dt>{t("sales.composer.total")}</dt>
              <dd className="num">{formatMinor(totals.totalMinor, currency, i18n.language)}</dd>
            </div>
          ) : null}
        </dl>
      )}
      {vatKnown ? (
        <p className="sales-hint" data-testid="sales-vat-mode">
          {vatMode === "inclusive"
            ? t("sales.composer.vatInclusive")
            : t("sales.composer.vatExclusive")}
        </p>
      ) : (
        <p className="sales-hint" data-testid="sales-vat-unknown">
          {/* The press is a PLACEMENT in order mode, not a draft save — the
              quote wording would promise a save step this document lacks. */}
          {t(isOrder ? "sales.composer.vatOnPlace" : "sales.composer.vatOnSave")}
        </p>
      )}
      {serverTotals === undefined && !totalsWithheld ? (
        <p className="sales-hint" data-testid="sales-totals-preview">
          {t(isOrder ? "sales.composer.totalsPreviewOrder" : "sales.composer.totalsPreview")}
        </p>
      ) : null}
      {/* A sum that silently omits an unpriced line is a wrong number, not a
          partial one, and it is the one figure a manager repeats to a customer
          out loud. Said, not inferred — the same standard the price cells hold. */}
      {serverTotals === undefined && !totalsWithheld && unpricedRows > 0 ? (
        <p className="sales-hint" data-testid="sales-totals-incomplete">
          {t("sales.composer.totalsIncomplete", { count: unpricedRows })}
        </p>
      ) : null}

      {/* ORDER MODE's header fields (P3-D PR-D1): the facility picker and the
          customer's PO number. NOT validity, NOT a note to the customer, NOT
          an internal note — `sales_orders` has no such columns, and a field
          the wire cannot carry is a field the manager fills in for nothing. */}
      {editable && isOrder ? (
        <div className="filter-bar" data-testid="sales-order-fields">
          {facilityItems.length > 1 ? (
            <Field label={t("sales.composer.facility")}>
              <Select
                value={facilityId}
                data-testid="composer-facility"
                onChange={(e) => {
                  setFacilityId(e.target.value);
                }}
              >
                <option value="">{t("sales.composer.facilityDerived")}</option>
                {facilityItems.map((facility) => (
                  <option key={facility.id} value={facility.id}>
                    {facility.name}
                  </option>
                ))}
              </Select>
            </Field>
          ) : (
            // ONE active facility (or none the reader can see): no select with
            // nothing to choose — module 06's rule — and the sentence says
            // where the order will be fulfilled from instead.
            <p className="sales-hint" data-testid="sales-order-facility-derived">
              {t("sales.composer.facilityDerivedNote")}
            </p>
          )}
          <Field label={t("sales.composer.poRef")} wide>
            <input
              value={poRef}
              data-testid="composer-po-ref"
              onChange={(e) => {
                setPoRef(e.target.value);
                setDirty(true);
              }}
            />
          </Field>
        </div>
      ) : null}

      {editable && !isOrder ? (
        <div className="filter-bar">
          <Field label={t("sales.composer.validity")}>
            <input
              className="num"
              value={validityDays}
              inputMode="numeric"
              data-testid="composer-validity"
              onChange={(e) => {
                setValidityDays(e.target.value);
                setDirty(true);
              }}
            />
          </Field>
          {/* Each note takes the bar's whole row (`.sales-composer-note`,
              sales.css): a note is a sentence, and at the 16rem `wide` floor
              a seeded one no longer fitted its own box. */}
          <Field label={t("sales.composer.noteToCustomer")} wide className="sales-composer-note">
            <input
              value={noteToCustomer}
              data-testid="composer-note"
              onChange={(e) => {
                setNoteToCustomer(e.target.value);
                setDirty(true);
              }}
            />
          </Field>
          <Field label={t("sales.composer.internalNote")} wide className="sales-composer-note">
            <input
              value={internalNote}
              placeholder={t("sales.composer.internalNoteHint")}
              data-testid="composer-internal-note"
              onChange={(e) => {
                setInternalNote(e.target.value);
                setDirty(true);
              }}
            />
          </Field>
        </div>
      ) : null}

      {revisionEdit && dirty ? (
        <p className="sales-banner" role="status" data-testid="sales-revision-warning">
          {/* `quote` is DEFINED here, and not by luck: `revisionEdit` is the
              aliased condition `quote !== undefined && isRevisionEdit(...)`,
              which narrows it. The `?? 0` this line used to carry was not a
              safety net but a wrong answer waiting for an unreachable branch —
              it would have promised the manager "revision 1" over a document
              whose revision number could not be read. */}
          {t("sales.composer.reviseHint", { next: quote.current_revision_no + 1 })}
        </p>
      ) : null}

      {create.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={createDealRefusalText(t, create.error) ?? t("sales.composer.saveFailed")}
          error={create.error}
        />
      ) : null}
      {createOrder.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.composer.orderFailed")}
          error={createOrder.error}
        />
      ) : null}
      {revise.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.composer.saveFailed")}
          error={revise.error}
        />
      ) : null}
      {send.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={sendFailureText(t, send.error)}
          error={send.error}
        />
      ) : null}
      {duplicate.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.composer.duplicateFailed")}
          error={duplicate.error}
        />
      ) : null}
      {cancelQuote.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.composer.cancelFailed")}
          error={cancelQuote.error}
        />
      ) : null}
      {recordRejection.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.composer.rejectFailed")}
          error={recordRejection.error}
        />
      ) : null}

      {sendResult !== undefined ? (
        <p className="sales-banner" role="status" data-testid="sales-send-result">
          {sendResult.status === "sent"
            ? `${t("sales.composer.sentTitle")} — ${t("sales.composer.sentBody")}`
            : `${t("sales.composer.pendingTitle")} — ${t("sales.composer.pendingBody")}`}
          {/* The sent card carries the document that went with the send
              (D-288): the manager's own copy, the moment they send it. Its
              testid is the card's own, so it never doubles the header's
              `sales-quote-pdf` once the refetched quote reads `sent`. A send
              with no PDF id (none since the renderer landed; the wire keeps
              the field nullable) gets no link, because the door would 409. */}
          {sendResult.status === "sent" &&
          sendResult.pdf_file_id !== null &&
          perms.has("sales.read_value") ? (
            <>
              {" "}
              <a
                className="ds-lnk"
                href={quotePdfPath(quoteId)}
                target="_blank"
                rel="noreferrer"
                data-testid="sales-send-result-pdf"
              >
                {t("sales.quotes.pdf")}
              </a>
            </>
          ) : null}
        </p>
      ) : null}

      {/* WHY SAVE IS REFUSING, beside the button that is refusing. A disabled
          control with no reason is the same silence the drop itself was. */}
      {editable && dirty && incompleteRows > 0 ? (
        <p className="sales-hint" role="status" data-testid="sales-lines-incomplete">
          {t("sales.composer.linesIncomplete", { count: incompleteRows })}
          {lineDefects.map((entry) => (
            <span
              key={entry.line}
              className="sales-line-defect"
              data-testid="sales-line-defect"
              data-line={String(entry.line)}
              data-defect={entry.defect}
            >
              {entry.product.length > 0
                ? t(
                    entry.defect === "qty"
                      ? "sales.composer.lineDefectQty"
                      : "sales.composer.lineDefectPrice",
                    { line: entry.line, product: entry.product },
                  )
                : t(
                    entry.defect === "qty"
                      ? "sales.composer.lineDefectQtyNoProduct"
                      : "sales.composer.lineDefectPriceNoProduct",
                    { line: entry.line },
                  )}
            </span>
          ))}
        </p>
      ) : null}
      {/* The other reason Save refuses (D-337), stated whether or not the
          grid is dirty, so the writer learns it before editing a line rather
          than after. */}
      {editable && !isOrder && reviseValueHidden ? (
        <p className="sales-hint" role="status" data-testid="sales-revise-value-hidden">
          {t("sales.composer.reviseValueHidden")}
        </p>
      ) : null}

      <div className="sales-toolbar">
        {/* ORDER MODE's ONE verb (P3-D PR-D1, D-283). It rides `editable` for
            §4.4's mobile rule (no editors, no placement on a phone) and
            `payloadValid` for the same refusal Save makes — a row the grid
            shows but the payload would drop must be fixed, not dropped. NO
            `dirty` gate: there is no saved document to be un-dirty against,
            and a placement is not a re-send. The LABEL says what the press
            will do — confirm the order, or park it for the desk §3.4 names. */}
        {editable && isOrder ? (
          <Button
            variant="primary"
            data-testid="sales-order-place"
            disabled={!payloadValid}
            busy={createOrder.isPending}
            busyLabel={t("sales.composer.placing")}
            onClick={onPlace}
          >
            {needsReview
              ? t("sales.composer.placeOrderForApproval")
              : t("sales.composer.placeOrder")}
          </Button>
        ) : null}
        {/* SAVE RIDES `editable`, not `canWrite && !terminal`: the editors are
            absent on a phone, so a Save there could only ever resend the
            document unchanged — which on a `sent` quote is not a no-op but
            revision n+1, revoking the customer's live link. `dirty` is the
            second half of the same guard on the desktop: nothing edited,
            nothing to save. */}
        {editable && !isOrder ? (
          <Button
            variant="primary"
            data-testid="sales-quote-save"
            disabled={!payloadValid || !dirty || reviseValueHidden}
            busy={create.isPending || revise.isPending}
            busyLabel={t("sales.composer.saving")}
            onClick={onSave}
          >
            {revisionEdit ? t("sales.composer.revise") : t("sales.composer.saveDraft")}
          </Button>
        ) : null}
        {canSend && quote !== undefined && !terminal && !expired ? (
          <Button
            variant={needsReview ? "secondary" : "primary"}
            data-testid="sales-quote-send"
            disabled={dirty}
            busy={send.isPending}
            busyLabel={t("sales.composer.sending")}
            onClick={() => {
              send.mutate({});
            }}
          >
            {/* §4.4: the button SAYS what pressing it will do. A breached
                document that has not been decided does not "send" — it enters
                review, and calling that Send is how a manager discovers the
                difference from a banner afterwards instead of from the control
                beforehand. A DECIDED one sends, and must not keep saying
                otherwise. */}
            {needsReview ? t("sales.composer.sendForApproval") : t("sales.composer.send")}
          </Button>
        ) : null}
      </div>
      {needsReview && canSend && !expired ? (
        <p className="sales-hint" data-testid="sales-send-approval-hint">
          {t("sales.composer.sendApprovalHint")}
        </p>
      ) : null}
      {/* The order mode's twin of the send hint: the label says "for
          approval", this says why and what happens next (nothing is reserved
          and no credit verdict is stamped until the desk decides — §3.6). */}
      {isOrder && editable && needsReview ? (
        <p className="sales-hint" data-testid="sales-order-approval-hint">
          {t("sales.composer.orderApprovalHint")}
        </p>
      ) : null}
      {/* D-268: the send route answers 409 QUOTE_EXPIRED here, so the screen
          offers the one verb §3.4 leaves — revision n+1 at current lists — and
          says why, instead of leaving a Send that refuses on the glass.

          AND THE REMEDY IS CONDITIONAL (task #229). "Save it as a new version"
          is the right instruction only while that door opens: on an archived
          customer's expired quote — which is the population §3.1.4's archive
          guard actively creates, since `expired` is one of the statuses it
          treats as finished with — the new version is refused too, and this
          note was sending the manager to the second of two dead buttons. The
          short form states the fact alone and leaves the remedy to the
          customer note below, which is the one that can name it. */}
      {expired ? (
        <p className="sales-hint" data-testid="sales-expired-note">
          {t(customerQuotable ? "sales.composer.expiredNote" : "sales.composer.expiredNoteBlocked")}
        </p>
      ) : null}
      {/* WHY BOTH WRITE DOORS ARE MISSING (task #229, D-118's other half: a
          control that does not exist says so). Said only to a reader who would
          otherwise have been offered one — `sales.quote.write` on a desktop,
          on a saved quote — so a read-only reader and a phone, which are
          missing those verbs for their own stated reasons, get no second
          sentence about a rule that was never going to reach them. The cause
          decides the sentence: an archived customer points to another
          customer, since no customer restore exists yet (it is designed with
          P3-E2-2's merge, D-250, and #277 keeps the note from advising one),
          and an assignment is an administrator's to give back. */}
      {quote !== undefined && canWrite && isDesktop && !customerQuotable ? (
        <p className="sales-hint" role="status" data-testid="sales-customer-blocked-note">
          {t(
            quoteCustomerArchived(quote)
              ? "sales.composer.customerArchivedNote"
              : "sales.composer.customerUnassignedNote",
          )}
        </p>
      ) : null}

      {/* Attached files (E2-2a2, spec 09 §4.4's annotation, D-323 ruling 8):
          on a SAVED quote, with Upload and Detach for a holder of
          `sales.quote.write` (the quote door that loaded this quote admits
          it). An archived customer's quote takes no new file and says so in
          place of Upload, as the attach would answer 422. The list reads for
          every reader of the composer, and qc and the storekeeper, who hold
          the quote door without the composer, reach a quote's technical
          files through the list route alone (§7 narrowing 19). */}
      {!isOrder && quote !== undefined ? (
        <AttachmentsPanel
          subject={{ kind: "quote", id: quote.id }}
          canWrite={canAttachTo(perms, "quote")}
          writeClosedNote={
            quoteCustomerArchived(quote) ? t("sales.attachments.customerArchived") : undefined
          }
        />
      ) : null}

      {/* What the customer was told about THIS quote (P3-E PR-E1, D-291) —
          on every saved quote, draft included, because a draft made from a
          customer's request has already queued their acknowledgement. Its
          own component, so the read mounts only where the card does. */}
      {!isOrder && quote !== undefined ? <QuoteNotificationsCard quoteId={quote.id} /> : null}

      {/* The quote's OPEN TASKS (P3-E PR-E2, D-292 ruling 1) — a
          `QUOTE_NO_ANSWER` task is what the follow-up sequence leaves when
          its nudges run out. On a SAVED quote only (a new one has no id to
          hang a task on); the card gates itself on `sales.activity.write`. */}
      {!isOrder && quote !== undefined ? (
        <OpenTasksCard
          subject={{ kind: "quote", id: quote.id, customerId: quote.customer_id }}
          label={quote.quote_no}
        />
      ) : null}
    </section>
  );
}

/**
 * §4.4's Notifications card (P3-E PR-E1, D-291): `GET /v1/customer-
 * notifications?quote_id=`, newest first, in the §4.7 panel's own table so a
 * held, superseded or failed row reads the same on both documents. Loading,
 * error and empty are each stated; a second page is offered, never cut off;
 * and the read refreshes itself while a row can still move — the
 * acknowledgement is queued at the draft's creation and sent seconds later.
 *
 * The error card shows only when there is nothing to show: once rows are on
 * the glass, a failed background refresh leaves them there (the order
 * detail's rule, for the same reason).
 */
function QuoteNotificationsCard({ quoteId }: { readonly quoteId: string }) {
  const { t, i18n } = useTranslation();
  const notifications = useQuoteNotifications(quoteId);
  // One page at a time (D-560): Next reads the following page when it is not
  // loaded yet, and every loaded page is still re-read on the bounded poll.
  const pager = useLoadedPager(notifications, quoteId);
  const rows = pager.view.items;
  return (
    <Card
      className="sales-360-card"
      data-testid="sales-quote-notifications"
      title={t("sales.composer.notificationsTitle")}
    >
      {notifications.data === undefined ? (
        notifications.error !== null ? (
          <div data-testid="sales-quote-notifications-error">
            <ErrorState
              title={t("sales.composer.notificationsError")}
              retryLabel={t("sales.common.retry")}
              requestIdLabel={t("sales.common.requestId")}
              requestId={requestIdOf(notifications.error)}
              onRetry={() => {
                void notifications.refetch();
              }}
            />
          </div>
        ) : (
          <div data-testid="sales-quote-notifications-loading">
            <CardSkeleton lines={2} label={t("sales.common.loading")} />
          </div>
        )
      ) : (
        <>
          <CustomerNotificationTable
            rows={rows}
            tableTestId="sales-quote-notification-table"
            rowTestId="sales-quote-notification"
            caption={t("sales.composer.notificationsTitle")}
            empty={
              <p className="sales-hint" data-testid="sales-quote-notifications-empty">
                {t("sales.composer.notificationsEmpty")}
              </p>
            }
            pager={bookPager(t, i18n.language, pager.view, {
              turning: pager.turning,
              onNext: pager.next,
              onPrev: pager.prev,
            })}
          />
        </>
      )}
    </Card>
  );
}

/** §3.3's panel: the ranked off-cut options for one line, each naming its
 * physical pieces and what applying it saves. */
function OffcutPanel({
  row,
  currency,
  onApply,
  onClose,
}: {
  row: GridRow | undefined;
  currency: string;
  onApply: (plan: QuoteLineInput["fulfillment_plan"]) => void;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const options = row?.fulfillment?.offcut_options ?? [];
  // WHY THE LIST IS EMPTY, in the server's words (D-301 round 7). Each option
  // carries a price, so a reader who may not see prices is served none of
  // them, and the answer says so with `offcut_options_withheld`. Without the
  // flag the list is empty because nothing is offered. Amber means pieces on
  // the shelves cover the line, so an empty list beside it says no off-cut
  // discount applies to them, never that no combination exists.
  const withheld = row?.fulfillment?.offcut_options_withheld === true;
  const coveredByPieces = row?.fulfillment?.light === "amber";
  return (
    <Card
      className="sales-offcut-panel"
      data-testid="sales-offcut-panel"
      title={t("sales.composer.offcutTitle")}
      action={
        <Button size="sm" onClick={onClose}>
          {t("sales.composer.offcutClose")}
        </Button>
      }
    >
      {options.length === 0 && withheld ? (
        <p className="sales-hint" data-testid="sales-offcut-value-hidden">
          {t("sales.composer.offcutValueHidden")}
        </p>
      ) : options.length === 0 && coveredByPieces ? (
        <p className="sales-hint" data-testid="sales-offcut-undiscounted">
          {t("sales.composer.offcutUndiscounted")}
        </p>
      ) : options.length === 0 ? (
        <p className="sales-hint" data-testid="sales-offcut-none">
          {t("sales.composer.offcutNone")}
        </p>
      ) : (
        <ul className="sales-offcut-list">
          {options.map((option) => (
            <li key={option.offcut_class_id} data-testid="sales-offcut-option">
              <span>
                {t("sales.composer.offcutOption", {
                  qty: row?.qtyText ?? "",
                  pieces: option.pieces.map((p) => p.length).join(" + "),
                  pct: option.discount_pct,
                })}
              </span>
              {option.pieces[0]?.location_code != null ? (
                <span className="sales-provisional">
                  {t("sales.composer.offcutWhere", { location: option.pieces[0].location_code })}
                </span>
              ) : null}
              <span className="num">
                {formatMinor(option.line_saving_minor, currency, i18n.language)}
              </span>
              <Button
                size="sm"
                variant="primary"
                data-testid="sales-offcut-apply"
                onClick={() => {
                  onApply([
                    {
                      kind: "offcut_combo",
                      pieces: option.pieces.map((p) => ({
                        stock_unit_id: p.stock_unit_id,
                        length: p.length,
                      })),
                    },
                  ] as QuoteLineInput["fulfillment_plan"]);
                }}
              >
                {t("sales.composer.offcutApply")}
              </Button>
            </li>
          ))}
        </ul>
      )}
      <p className="sales-hint">{t("sales.composer.offcutSaves")}</p>
    </Card>
  );
}

/** §3.4's states from acceptance on: the date is then module 03's to confirm. */
function isAcceptedOrLater(status: string): boolean {
  return status === "accepted" || status === "converted";
}

/**
 * One quote line's delivery date: the date Check date wrote and its Quoted
 * chip, the save's refusal of it in words, and the two verbs. Check date is
 * drawn only for a reader who may run the dialog, on a line with a product
 * and a quantity; Remove date appears only beside a refused date.
 */
function DeliveryCell({
  row,
  quoted,
  refused,
  canCheck,
  checkable,
  onCheck,
  onClear,
}: {
  row: GridRow;
  quoted: boolean;
  refused: QuoteLinePromiseReason | undefined;
  canCheck: boolean;
  checkable: boolean;
  onCheck: () => void;
  onClear: () => void;
}) {
  const { t, i18n } = useTranslation();
  return (
    <span className="sales-line-delivery">
      {row.promiseHint !== undefined ? (
        <span
          data-testid="composer-line-delivery-hint"
          data-date={row.promiseHint}
          data-promise-id={row.promiseId}
        >
          {t("sales.composer.deliveryBy", {
            date: formatBusinessDate(row.promiseHint, i18n.language),
          })}{" "}
          {quoted ? (
            <StatusChip variant="neutral" dot={false} data-testid="composer-line-delivery-quoted">
              {t("sales.composer.deliveryQuoted")}
            </StatusChip>
          ) : null}
        </span>
      ) : null}
      {refused !== undefined ? (
        <span className="field-error" role="alert" data-testid="composer-line-delivery-refused">
          {t(`sales.composer.deliveryRefused.${refused}`)}
        </span>
      ) : null}
      {canCheck ? (
        <Button
          size="sm"
          disabled={!checkable}
          data-testid="composer-line-check-date"
          onClick={onCheck}
        >
          {t("sales.composer.checkDate")}
        </Button>
      ) : null}
      {canCheck && refused !== undefined && row.promiseId !== undefined ? (
        <Button size="sm" data-testid="composer-line-promise-clear" onClick={onClear}>
          {t("sales.composer.deliveryRemove")}
        </Button>
      ) : null}
    </span>
  );
}

/**
 * The CTP dialog for one line (D-474 ruling 2, through `planning/public.ts`).
 * It waits for the product so it knows the base unit: the dialog quotes in
 * the base unit, so a line in that unit opens with its quantity and any other
 * opens empty beside the base unit's code. Use in quote then hands the quote
 * and the base unit back, and the line takes the checked quantity in it.
 */
function CheckDateDialog({
  row,
  quoteRef,
  onUse,
  onClose,
}: {
  row: GridRow | undefined;
  quoteRef: string | undefined;
  onUse: (quote: CtpQuoteResult, baseUomId: string | undefined) => void;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const product = useProduct(row?.productId ?? "");
  if (row === undefined) return null;
  // The dialog quotes in the product's base unit, so it opens only once that
  // unit is known: a dialog opened without it would let Use in quote write the
  // base quantity onto the line's own unit.
  if (product.isPending) {
    return (
      <div data-testid="sales-check-date-loading">
        <CardSkeleton lines={1} label={t("sales.common.loading")} />
      </div>
    );
  }
  if (product.data === undefined) {
    return (
      <div data-testid="sales-check-date-error">
        <ErrorState
          title={t("sales.composer.checkDateProductError")}
          retryLabel={t("sales.common.retry")}
          requestIdLabel={t("sales.common.requestId")}
          requestId={requestIdOf(product.error)}
          onRetry={() => {
            void product.refetch();
          }}
        />
        <Button variant="secondary" data-testid="sales-check-date-error-close" onClick={onClose}>
          {t("sales.common.close")}
        </Button>
      </div>
    );
  }
  const baseUomId = product.data.base_uom_id;
  const qty = normalizeTypedDecimal(row.qtyText.trim(), i18n.language);
  const inBase = baseUomId === row.uomId && QTY_TEXT.test(qty);
  return (
    <CtpDialog
      productId={row.productId}
      qty={inBase ? qty : undefined}
      quoteRef={quoteRef}
      quoteLineId={row.server?.id}
      onUse={(quote) => {
        onUse(quote, baseUomId);
      }}
      onClose={onClose}
    />
  );
}

/** The add-row control — the price-list grid's `DraftEditor` shape: an exact
 * SKU match, a quantity and a UoM, confirmed into the grid above. */
function AddLineRow({ onAdd }: { onAdd: (row: GridRow) => void }) {
  const { t, i18n } = useTranslation();
  const [skuText, setSkuText] = useState("");
  const [qty, setQty] = useState("");
  const [uomId, setUomId] = useState("");
  const uoms = useUoms();
  const search = useProductSearch(skuText.trim());
  const match = useMemo(() => {
    const needle = skuText.trim().toLowerCase();
    if (needle.length === 0) return undefined;
    return (search.data?.items ?? []).find((p) => p.sku.toLowerCase() === needle);
  }, [search.data, skuText]);
  const effectiveUom = uomId.length > 0 ? uomId : (match?.base_uom_id ?? "");
  // The add row reads the same keyboard as the grid it adds to (D-274) —
  // otherwise Add stays disabled on a quantity the grid would have accepted.
  const ready = match !== undefined && qtyTypedOk(qty, i18n.language) && effectiveUom.length > 0;

  return (
    <div className="filter-bar sales-grid-add" data-testid="sales-line-add">
      <Field label={t("sales.composer.product")} wide>
        <input
          value={skuText}
          placeholder={t("sales.composer.productHint")}
          data-testid="sales-line-add-sku"
          onChange={(e) => {
            setSkuText(e.target.value);
          }}
        />
      </Field>
      {skuText.trim().length > 0 && match === undefined && !search.isPending ? (
        <p className="sales-applied" data-testid="sales-line-add-nomatch">
          {t("sales.composer.productNoMatch")}
        </p>
      ) : null}
      <Field label={t("sales.composer.qty")}>
        <input
          className="num"
          value={qty}
          inputMode="decimal"
          data-testid="sales-line-add-qty"
          onChange={(e) => {
            setQty(e.target.value);
          }}
        />
      </Field>
      <Field label={t("sales.composer.uom")}>
        <Select
          value={effectiveUom}
          data-testid="sales-line-add-uom"
          onChange={(e) => {
            setUomId(e.target.value);
          }}
        >
          <option value="">{t("sales.composer.uom")}</option>
          {(uoms.data?.items ?? []).map((u) => (
            <option key={u.id} value={u.id}>
              {u.code}
            </option>
          ))}
        </Select>
      </Field>
      <Button
        variant="primary"
        size="sm"
        disabled={!ready}
        data-testid="sales-line-add-confirm"
        onClick={() => {
          if (match === undefined) return;
          onAdd({
            key: rowKey(),
            productId: match.id,
            productLabel: `${match.sku} · ${match.name}`,
            uomId: effectiveUom,
            qtyText: normalizeTypedDecimal(qty.trim(), i18n.language),
            priceText: "",
            server: undefined,
            fulfillment: undefined,
            plan: undefined,
            // No save has put anything here — the row is not in the document.
            serverPriceText: undefined,
            promiseDate: "",
            promiseId: undefined,
            promiseHint: undefined,
          });
          setSkuText("");
          setQty("");
          setUomId("");
        }}
      >
        {t("sales.composer.addLine")}
      </Button>
    </div>
  );
}
